import React from 'react';
import { getObstacleIcon } from '../../utils/markers';
import { T } from '../../assets/theme';
import { getRunwayProfile } from '../../api/client';
import RunwayProfileChart from '../../components/Report/RunwayProfileChart';
import AeroOlsReport from '../../components/Report/AeroOlsReport';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// ──────────────────────────────────────────────────────────────────────────────
// Module-scope helpers (don't close over component state — safe to hoist)
// ──────────────────────────────────────────────────────────────────────────────

function toDMS(deg, isLat) {
    if (deg == null || isNaN(deg)) return '—';
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const mFull = (abs - d) * 60;
    const m = Math.floor(mFull);
    const s = ((mFull - m) * 60).toFixed(4);
    const dir = isLat ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
    return `${d}° ${m}' ${s}" ${dir}`;
}

// Minor ≤ 5 m, Major > 5 m — blank for non-penetrating rows.
function penetrationComment(isPenetrated, penetrationM) {
    if (!isPenetrated || penetrationM == null) return '';
    return penetrationM <= 5 ? 'Minor' : 'Major';
}

function R({ label, value }) {
    return (
        <tr>
            <td style={{ padding: '4px 12px', color: T.muted, width: '45%', fontSize: 11, border: `1px solid ${T.border}`, verticalAlign: 'top' }}>{label}</td>
            <td style={{ padding: '4px 12px', color: T.white, fontSize: 11, border: `1px solid ${T.border}` }}>{value || '—'}</td>
        </tr>
    );
}

function SectionTitle({ n, title, id }) {
    return (
        <h2 id={id} style={{ margin: '28px 0 12px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, fontWeight: 800, color: T.white, letterSpacing: 0.3 }}>
            <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(58,138,196,0.15)', border: `1px solid ${T.blue}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: T.blue, flexShrink: 0 }}>{n}</span>
            <span>{n}. {title}</span>
            <span aria-hidden="true" style={{ flex: 1, height: 1, background: `${T.border}` }} />
        </h2>
    );
}

function SubHeading({ children, level = 3, style }) {
    const Tag = `h${level}`;
    return <Tag style={{ color: T.blue, margin: '20px 0 10px', fontSize: 13, fontWeight: 700, ...style }}>{children}</Tag>;
}

function StatusCell({ s }) {
    const bySt = {
        penetrated: '#e84040',
        'CNS Engineer': '#f5a623',
        'not penetrated': '#2EC98B',
        'outside surfaces': T.muted,
        clear: '#2EC98B',
        YES: '#e84040',
        No: '#2EC98B',
        'N/A': T.muted,
        outside: T.muted
    };
    const c = bySt[s] || T.muted;
    return <td style={{ padding: '5px 10px', border: `1px solid ${T.border}`, color: c, fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap', textAlign: 'center' }}>{s || '—'}</td>;
}

function TblHead({ cols }) {
    return (
        <thead>
            <tr style={{ background: 'rgba(58,138,196,0.12)' }}>
                {cols.map(c => (
                    <th key={c} scope="col" style={{ padding: '6px 10px', border: `1px solid ${T.border}`, textAlign: 'center', fontWeight: 700, fontSize: 10, color: T.blue, whiteSpace: 'nowrap' }}>{c}</th>
                ))}
            </tr>
        </thead>
    );
}

function Td({ v, gray }) {
    return <td style={{ padding: '5px 10px', border: `1px solid ${T.border}`, fontSize: 10, color: gray ? T.muted : T.white, textAlign: 'center' }}>{v ?? '—'}</td>;
}

// ──────────────────────────────────────────────────────────────────────────────
// Map image components 
// ──────────────────────────────────────────────────────────────────────────────

function OlsMapImage({ icao, type = 'ols', projectId }) {
    const [ts, setTs] = React.useState(() => Date.now());
    const [broken, setBroken] = React.useState(false);
    const src = `${API_BASE}/ols-surfaces/${icao}/screenshot?type=${type}&project_id=${projectId}&t=${ts}`;

    function retry() { setBroken(false); setTs(Date.now()); }

    if (broken) {
        return (
            <div style={{ width: '100%', height: 120, background: 'rgba(255,255,255,0.03)', border: `1px dashed ${T.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 14, gap: 8 }}>
                <span style={{ color: T.muted, fontSize: 11 }}>Go to Annex 14 OLS tab → click {type.toUpperCase()} next to {icao} to capture map</span>
                <button onClick={retry} aria-label={`Refresh ${type.toUpperCase()} map for ${icao}`} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'rgba(58,138,196,0.12)', color: T.blue, cursor: 'pointer' }}>
                    <span aria-hidden="true">↻</span> Refresh image
                </button>
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={`${type.toUpperCase()} surface map for ${icao}`}
            onError={() => setBroken(true)}
            style={{ width: '13cm', height: '11cm', objectFit: 'cover', borderRadius: 8, marginBottom: 14, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 14px' }}
        />
    );
}

function CirclingMapImage({ icao, projectId }) {
    const [ts, setTs] = React.useState(() => Date.now());
    const [broken, setBroken] = React.useState(false);
    const src = `${API_BASE}/circling/screenshot/${icao}?project_id=${projectId}&t=${ts}`;

    function retry() { setBroken(false); setTs(Date.now()); }

    if (broken) {
        return (
            <div style={{ width: '13cm', height: '11cm', background: 'rgba(255,255,255,0.03)', border: `1px dashed ${T.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 14, gap: 8, margin: '0 auto 14px' }}>
                <span style={{ color: T.muted, fontSize: 11 }}>Go to Circling tab → click "Capture Screenshot" next to {icao} to capture map</span>
                <button onClick={retry} aria-label={`Refresh circling map for ${icao}`} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'rgba(58,138,196,0.12)', color: T.blue, cursor: 'pointer' }}>
                    <span aria-hidden="true">↻</span> Refresh image
                </button>
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={`Circling area map for ${icao}`}
            onError={() => setBroken(true)}
            style={{ width: '13cm', height: '11cm', objectFit: 'cover', borderRadius: 8, marginBottom: 14, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 14px' }}
        />
    );
}

function PansIlsMapImage({ icao, runway, category, projectId }) {
    const [ts, setTs] = React.useState(() => Date.now());
    const [broken, setBroken] = React.useState(false);
    const src = `${API_BASE}/pans/ils-surfaces/${icao}_${runway}_${category}/screenshot?project_id=${projectId}&t=${ts}`;

    function retry() { setBroken(false); setTs(Date.now()); }

    if (broken) {
        return (
            <div style={{ width: '100%', height: 120, background: 'rgba(255,255,255,0.03)', border: `1px dashed ${T.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 14, gap: 8 }}>
                <span style={{ color: T.muted, fontSize: 11 }}>Go to Flight Procedures tab → click "Capture Screenshot" next to {icao} RWY {runway} CAT {category} to capture map</span>
                <button onClick={retry} aria-label={`Refresh ILS map for ${icao} runway ${runway} category ${category}`} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'rgba(58,138,196,0.12)', color: T.blue, cursor: 'pointer' }}>
                    <span aria-hidden="true">↻</span> Refresh image
                </button>
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={`ILS surface map for ${icao} runway ${runway} category ${category}`}
            onError={() => setBroken(true)}
            style={{ width: '13cm', height: '11cm', objectFit: 'cover', borderRadius: 8, marginBottom: 14, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 14px' }}
        />
    );
}

function NpaMapImage({ icao, projectId }) {
    const [ts, setTs] = React.useState(() => Date.now());
    const [broken, setBroken] = React.useState(false);
    const src = `${API_BASE}/npa/screenshot/${icao}?project_id=${projectId}&t=${ts}`;
    function retry() { setBroken(false); setTs(Date.now()); }
    if (broken) {
        return (
            <div style={{ width: '100%', height: 120, background: 'rgba(255,255,255,0.03)', border: `1px dashed ${T.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 14, gap: 8 }}>
                <span style={{ color: T.muted, fontSize: 11 }}>Go to NPA tab → click "Capture Screenshot" next to {icao} to capture map</span>
                <button onClick={retry} aria-label={`Refresh NPA map for ${icao}`} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'rgba(58,138,196,0.12)', color: T.blue, cursor: 'pointer' }}>
                    <span aria-hidden="true">↻</span> Refresh image
                </button>
            </div>
        );
    }
    return (
        <img src={src} alt={`Non-precision approach map for ${icao}`} onError={() => setBroken(true)}
            style={{ width: '13cm', height: '11cm', objectFit: 'cover', borderRadius: 8, marginBottom: 14, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 14px' }} />
    );
}

function NpaRwyMapImage({ icao, rwy, projectId }) {
    const [ts, setTs] = React.useState(() => Date.now());
    const [broken, setBroken] = React.useState(false);
    const src = `${API_BASE}/npa/screenshot/rwy/${icao}/${rwy}?project_id=${projectId}&t=${ts}`;
    function retry() { setBroken(false); setTs(Date.now()); }
    if (broken) {
        return (
            <div style={{ width: '100%', height: 100, background: 'rgba(255,255,255,0.03)', border: `1px dashed ${T.border}`, borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 12, gap: 6 }}>
                <span style={{ color: T.muted, fontSize: 10 }}>Go to NPA tab → click "Capture Screenshot" on RWY {rwy} under {icao} to capture</span>
                <button onClick={retry} aria-label={`Refresh NPA runway ${rwy} map for ${icao}`} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'rgba(58,138,196,0.12)', color: T.blue, cursor: 'pointer' }}>
                    <span aria-hidden="true">↻</span> Refresh
                </button>
            </div>
        );
    }
    return (
        <img src={src} alt={`NPA runway ${rwy} map for ${icao}`} onError={() => setBroken(true)}
            style={{ width: '13cm', height: '10cm', objectFit: 'cover', borderRadius: 6, marginBottom: 12, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 12px' }} />
    );
}

function PreviewMapImage({ projectId }) {
    const [ts, setTs] = React.useState(() => Date.now());
    const [broken, setBroken] = React.useState(false);
    const src = `${API_BASE}/ols-surfaces/PROJECT_${projectId}/screenshot?type=preview&project_id=${projectId}&t=${ts}`;
    function retry() { setBroken(false); setTs(Date.now()); }
    if (broken) {
        return (
            <div style={{ width: '100%', height: 120, background: 'rgba(255,255,255,0.03)', border: `1px dashed ${T.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 14, gap: 8 }}>
                <span style={{ color: T.muted, fontSize: 11 }}>Go to Preview tab → click "Capture Screenshot" to capture map</span>
                <button onClick={retry} aria-label="Refresh project preview map" style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'rgba(58,138,196,0.12)', color: T.blue, cursor: 'pointer' }}>
                    <span aria-hidden="true">↻</span> Refresh image
                </button>
            </div>
        );
    }
    return (
        <img src={src} alt="Project preview map" onError={() => setBroken(true)}
            style={{ width: '13cm', height: '11cm', objectFit: 'cover', borderRadius: 8, marginBottom: 14, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 14px' }} />
    );
}

function VssMapImage({ icao, projectId }) {
    const [ts, setTs] = React.useState(() => Date.now());
    const [broken, setBroken] = React.useState(false);
    const src = `${API_BASE}/vss/screenshot/${icao}?project_id=${projectId}&t=${ts}`;
    function retry() { setBroken(false); setTs(Date.now()); }
    if (broken) {
        return (
            <div style={{ width: '100%', height: 120, background: 'rgba(255,255,255,0.03)', border: `1px dashed ${T.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 14, gap: 8 }}>
                <span style={{ color: T.muted, fontSize: 11 }}>Go to VSS tab → click "Capture Screenshot" next to {icao} to capture map</span>
                <button onClick={retry} aria-label={`Refresh VSS map for ${icao}`} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'rgba(58,138,196,0.12)', color: T.blue, cursor: 'pointer' }}>
                    <span aria-hidden="true">↻</span> Refresh image
                </button>
            </div>
        );
    }
    return (
        <img src={src} alt={`Visual segment surface map for ${icao}`} onError={() => setBroken(true)}
            style={{ width: '13cm', height: '11cm', objectFit: 'cover', borderRadius: 8, marginBottom: 14, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 14px' }} />
    );
}

function RnavMapImage({ icao, projectId }) {
    const [ts, setTs] = React.useState(() => Date.now());
    const [broken, setBroken] = React.useState(false);
    const src = `${API_BASE}/rnav/screenshot/${icao}?project_id=${projectId}&t=${ts}`;
    function retry() { setBroken(false); setTs(Date.now()); }
    if (broken) {
        return (
            <div style={{ width: '100%', height: 120, background: 'rgba(255,255,255,0.03)', border: `1px dashed ${T.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 14, gap: 8 }}>
                <span style={{ color: T.muted, fontSize: 11 }}>Go to RNAV Routes tab → click "Capture Screenshot" next to {icao} to capture map</span>
                <button onClick={retry} aria-label={`Refresh RNAV Routes map for ${icao}`} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'rgba(58,138,196,0.12)', color: T.blue, cursor: 'pointer' }}>
                    <span aria-hidden="true">↻</span> Refresh image
                </button>
            </div>
        );
    }
    return (
        <img src={src} alt={`RNAV Routes map for ${icao}`} onError={() => setBroken(true)}
            style={{ width: '13cm', height: '11cm', objectFit: 'cover', borderRadius: 8, marginBottom: 14, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 14px' }} />
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Tabs (with ARIA)
// ──────────────────────────────────────────────────────────────────────────────

function AeroTabs({ tabs, active, onChange, label = 'Aerodrome selection' }) {
    if (!tabs || tabs.length <= 1) return null;
    return (
        <div role="tablist" aria-label={label} className="no-print" style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
            {tabs.map(tab => {
                const isActive = active === tab;
                return (
                    <button
                        key={tab}
                        role="tab"
                        aria-selected={isActive}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => onChange(tab)}
                        style={{
                            padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            border: `1px solid ${isActive ? T.blue : T.border}`,
                            background: isActive ? 'rgba(58,138,196,0.15)' : 'transparent',
                            color: isActive ? T.blue : T.muted,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {tab}
                    </button>
                );
            })}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// SortableTable
// ──────────────────────────────────────────────────────────────────────────────

function SortableTable({ headers, rows, filename = 'export.csv', caption }) {
    const [sortCol, setSortCol] = React.useState(null);
    const [sortDir, setSortDir] = React.useState('asc');

    const handleSort = React.useCallback((idx) => {
        if (sortCol !== idx) {
            setSortCol(idx);
            setSortDir('asc');
        } else if (sortDir === 'asc') {
            setSortDir('desc');
        } else {
            setSortCol(null);
            setSortDir('asc');
        }
    }, [sortCol, sortDir]);

    const sortedRows = React.useMemo(() => {
        if (sortCol === null) return rows;
        return [...rows].sort((a, b) => {
            let valA = a.sortKeys?.[sortCol] ?? a.cells[sortCol];
            let valB = b.sortKeys?.[sortCol] ?? b.cells[sortCol];

            const extractText = (v) => {
                if (v == null) return '';
                if (typeof v === 'string' || typeof v === 'number') return String(v);
                if (v?.props?.children !== undefined) {
                    const c = v.props.children;
                    if (Array.isArray(c)) return c.map(extractText).join('');
                    return extractText(c);
                }
                return String(v);
            };

            const strA = extractText(valA).trim();
            const strB = extractText(valB).trim();

            const numA = parseFloat(strA.replace(/[^0-9.-]/g, ''));
            const numB = parseFloat(strB.replace(/[^0-9.-]/g, ''));

            if (!isNaN(numA) && !isNaN(numB) && strA.match(/^[-]?[\d.]+$/) && strB.match(/^[-]?[\d.]+$/)) {
                return sortDir === 'asc' ? numA - numB : numB - numA;
            }

            return sortDir === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
        });
    }, [rows, sortCol, sortDir]);

    const exportCsv = React.useCallback(() => {
        const escapeCSV = (str) => {
            const s = String(str ?? '').replace(/"/g, '""');
            return /[,\n"]/.test(s) ? `"${s}"` : s;
        };
        const extractText = (v) => {
            if (v == null) return '';
            if (typeof v === 'string' || typeof v === 'number') return String(v);
            if (v?.props?.children !== undefined) {
                const c = v.props.children;
                if (Array.isArray(c)) return c.map(extractText).join('');
                return extractText(c);
            }
            return String(v);
        };
        const headerRow = headers.map(escapeCSV).join(',');
        const dataRows = sortedRows.map(row =>
            row.cells.map(cell => escapeCSV(extractText(cell))).join(',')
        ).join('\n');

        const blob = new Blob([headerRow + '\n' + dataRows], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }, [headers, sortedRows, filename]);

    const ariaSortFor = (idx) => {
        if (sortCol !== idx) return 'none';
        return sortDir === 'asc' ? 'ascending' : 'descending';
    };

    return (
        <div style={{ position: 'relative', marginBottom: 20 }}>
            {rows.length > 0 && (
                <button className="no-print" onClick={exportCsv} title="Export to CSV" aria-label="Export table to CSV"
                    style={{ position: 'absolute', top: -32, right: 0, padding: '4px 10px', fontSize: 10, fontWeight: 700, borderRadius: 4, border: `1px solid ${T.blue}44`, background: 'rgba(58,138,196,0.1)', color: T.blue, cursor: 'pointer', zIndex: 10 }}>
                    <span aria-hidden="true">⬇</span> CSV
                </button>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                {caption && <caption style={{ position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>{caption}</caption>}
                <thead>
                    <tr style={{ background: 'rgba(58,138,196,0.12)' }}>
                        {headers.map((h, i) => (
                            <th key={i} scope="col" aria-sort={ariaSortFor(i)}
                                style={{ padding: 0, border: `1px solid ${T.border}`, fontWeight: 700, fontSize: 10, color: T.blue, whiteSpace: 'nowrap' }}>
                                <button
                                    type="button"
                                    onClick={() => handleSort(i)}
                                    style={{
                                        width: '100%', padding: '6px 10px', background: 'transparent', border: 'none',
                                        color: T.blue, fontSize: 10, fontWeight: 700, cursor: 'pointer', textAlign: 'center',
                                        font: 'inherit'
                                    }}
                                >
                                    {h}{' '}
                                    <span aria-hidden="true">
                                        {sortCol === i ? (sortDir === 'asc' ? '↑' : '↓') : <span style={{ opacity: 0.3 }}>↕</span>}
                                    </span>
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((row, ri) => {
                        const isPen = row.isPenetrated === true;
                        return (
                            <tr key={ri} style={{ background: isPen ? 'rgba(232,64,64,0.08)' : (ri % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent') }}>
                                {row.cells.map((cell, ci) => (
                                    <td key={ci} style={{ padding: '5px 10px', border: `1px solid ${T.border}`, fontSize: 10, color: T.white, textAlign: 'center' }}>
                                        {ci === 0 && isPen && <span style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Penetration:</span>}
                                        {cell ?? '—'}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────────

export default function ReportPreview({ project, assessAll, loading, onRun, npaResults = {}, vssResults = {}, rnavResults = {} }) {
    const [finalized, setFinalized] = React.useState(() => {
        try { return localStorage.getItem(`report_finalized_${project?.id}`) === 'true'; } catch { return false; }
    });
    const [downloading, setDownloading] = React.useState(false);
    const [downloadProgress, setDownloadProgress] = React.useState(0);
    const [downloadingDocx, setDownloadingDocx] = React.useState(false);
    const [isPdfRender] = React.useState(false);

    const [olsTab, setOlsTab] = React.useState(0);
    const [ofzTab, setOfzTab] = React.useState(0);
    const [olsAdgTab, setOlsAdgTab] = React.useState(0);
    const [pansTab, setPansTab] = React.useState(0);
    const [braOmniTab, setBraOmniTab] = React.useState(0);
    const [braDirTab, setBraDirTab] = React.useState(0);
    const [rwyProfile, setRwyProfile] = React.useState(null);
    const [excludedSections, setExcludedSections] = React.useState(new Set());

    const reportRef = React.useRef(null);

    React.useEffect(() => {
        if (project?.id && project.project_type === 'annex_14_ols_assessment') {
            getRunwayProfile(project.id).then(res => setRwyProfile(res.data));
        }
    }, [project?.id, project?.project_type]);

    // Load persisted excluded sections from backend
    React.useEffect(() => {
        if (!project?.id) return;
        fetch(`${API_BASE}/reports/${project.id}/excluded-sections`)
            .then(r => r.json())
            .then(d => {
                if (d.excluded_sections && d.excluded_sections.length > 0) {
                    setExcludedSections(new Set(d.excluded_sections));
                }
            })
            .catch(() => {});
    }, [project?.id]);

    // Persist excluded sections to backend on change
    const toggleExclude = React.useCallback((key) => {
        setExcludedSections(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            // Persist to backend
            if (project?.id) {
                fetch(`${API_BASE}/reports/${project.id}/excluded-sections`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ excluded_sections: [...next] }),
                }).catch(() => {});
            }
            return next;
        });
    }, [project?.id]);

    // Exclude toggle button component
    const ExcludeBtn = React.useCallback(({ sectionKey, label }) => {
        const isExcl = excludedSections.has(sectionKey);
        return (
            <button
                className="no-print"
                onClick={(e) => { e.stopPropagation(); toggleExclude(sectionKey); }}
                title={isExcl ? `Show ${label} in report` : `Hide ${label} from report`}
                style={{
                    marginLeft: 8, padding: '2px 10px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                    cursor: 'pointer', border: `1px solid ${isExcl ? '#e84040' : T.border}`,
                    background: isExcl ? 'rgba(232,64,64,0.15)' : 'rgba(255,255,255,0.05)',
                    color: isExcl ? '#e84040' : T.muted, transition: 'all 0.2s',
                }}
            >
                {isExcl ? '✕ Excluded' : '👁 Include'}
            </button>
        );
    }, [excludedSections, toggleExclude]);

    const proj = assessAll?.project;
    const obstacles = assessAll?.obstacles ?? [];
    const ols_results = assessAll?.ols_results ?? [];
    const bra_results = assessAll?.bra_results ?? [];
    const pans_ops_results = assessAll?.pans_ops_results ?? [];
    const rmac_results = assessAll?.rmac_results ?? [];
    const ils_results = assessAll?.ils_results ?? [];
    const msa_results = assessAll?.msa_results ?? [];
    const circling_results = assessAll?.circling_results ?? [];
    const nearby_arps = assessAll?.nearby_arps ?? [];

    const isAnnex14Only = proj?.project_type === 'annex_14_ols_assessment';

    const downloadReport = React.useCallback(async () => {
        if (!proj) return;

        // For OLS-only projects the preview IS the report — print it directly so
        // the PDF matches pixel-for-pixel instead of the generic Jinja2 template.
        if (isAnnex14Only) {
            const reportEl = document.querySelector('.ols-report-wrapper');
            if (!reportEl) { alert('Report not loaded yet.'); return; }

            // Collect all <style> tags from the document (includes AeroOlsReport CSS)
            const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
                .map(el => el.tagName === 'STYLE' ? `<style>${el.textContent}</style>`
                          : `<link rel="stylesheet" href="${el.href}">`)
                .join('\n');

            const win = window.open('', '_blank', 'width=900,height=700');
            win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
                <title>${proj.name} — OLS Assessment Report</title>
                ${styles}
                <style>body{margin:0;padding:0;background:#fff;}</style>
            </head><body>${reportEl.outerHTML}</body></html>`);
            win.document.close();

            // Give images/fonts time to load then print
            win.onload = () => {
                setTimeout(() => {
                    win.focus();
                    win.print();
                    win.close();
                }, 800);
            };
            // Fallback if onload doesn't fire (already loaded)
            setTimeout(() => {
                if (!win.closed) {
                    win.focus();
                    win.print();
                    setTimeout(() => win.close(), 1000);
                }
            }, 2000);
            return;
        }

        setDownloading(true);
        setDownloadProgress(10);

        try {
            const response = await fetch(`${API_BASE}/reports/${proj.id}/pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ excluded_sections: [...excludedSections] }),
            });
            if (!response.ok) throw new Error(`Server error: ${response.statusText}`);

            setDownloadProgress(50);
            const blob = await response.blob();
            setDownloadProgress(90);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${proj.name.replace(/[^a-z0-9]/gi, '_')}_Formal_Report.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            setDownloadProgress(100);
        } catch (e) {
            console.error('PDF generation failed', e);
            alert('PDF generation failed: ' + e.message);
        } finally {
            setTimeout(() => {
                setDownloading(false);
                setDownloadProgress(0);
            }, 600);
        }
    }, [proj, excludedSections, isAnnex14Only]);

    const downloadDocx = React.useCallback(async () => {
        if (!proj) return;
        setDownloadingDocx(true);
        try {
            const response = await fetch(`${API_BASE}/reports/${proj.id}/docx`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ excluded_sections: [...excludedSections] }),
            });
            if (!response.ok) throw new Error(`Server error: ${response.statusText}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${proj.name.replace(/[^a-z0-9]/gi, '_')}_Report.docx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Word generation failed', e);
            alert('Word generation failed: ' + e.message);
        } finally {
            setDownloadingDocx(false);
        }
    }, [proj, excludedSections]);

    const consolidated = React.useMemo(() => {
        if (!assessAll) return [];
        return obstacles.map((obs, i) => {
            const o = ols_results.filter(r => r.obstacle_id === obs.id);
            const b = bra_results.filter(r => r.obstacle_id === obs.id);
            const p = pans_ops_results.filter(r => r.obstacle_id === obs.id);

            const olsSt = o.some(r => r.overall_status === 'penetrated') ? 'penetrated' : o.length ? 'clear' : 'N/A';
            const braSt = isAnnex14Only ? 'N/A' : (b.some(r => r.outcome === 'penetrated' || r.outcome === 'CNS Engineer') ? 'penetrated' : b.length ? 'clear' : 'N/A');
            const pansSt = isAnnex14Only ? 'N/A' : (p.some(r => r.status === 'penetrated') ? 'penetrated' : p.length ? 'clear' : 'N/A');
            const rmacSt = (isAnnex14Only || rmac_results.length === 0) ? 'N/A' : (rmac_results.some(r => r.is_penetration) ? 'penetrated' : 'clear');
            const msaSt = (isAnnex14Only || msa_results.length === 0) ? 'N/A' : (msa_results.some(r => r.results?.some(res => res.obstacle_id === obs.id && res.penetrates)) ? 'penetrated' : 'clear');
            const cirSt = (isAnnex14Only || circling_results.length === 0) ? 'N/A' : (circling_results.some(r => r.results?.some(res => res.obstacle_id === obs.id && res.penetrates)) ? 'penetrated' : 'clear');

            return { seq: i + 1, obs, olsSt, braSt, pansSt, msaSt, rmacSt, cirSt };
        });
    }, [assessAll, obstacles, ols_results, bra_results, pans_ops_results, rmac_results, msa_results, circling_results, isAnnex14Only]);

    if (loading) return (
        <div role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 16, color: T.white }}>
            <div aria-hidden="true" style={{ fontSize: 32, animation: 'spin 1s linear infinite' }}>⟳</div>
            <div style={{ fontSize: 14, color: T.muted }}>Generating report…</div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
    );

    if (!assessAll) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 16 }}>
            <div aria-hidden="true" style={{ fontSize: 48 }}>📄</div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: T.white, margin: 0 }}>Report not yet generated</h2>
            <div style={{ fontSize: 13, color: T.muted }}>Run the assessment first to populate the report.</div>
            <button onClick={onRun} style={{ marginTop: 8, padding: '10px 28px', borderRadius: 8, border: `1px solid ${T.safe}55`, background: 'rgba(46,201,139,0.15)', color: T.safe, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                <span aria-hidden="true">▶</span> Run Assessment
            </button>
        </div>
    );

    const now = new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' });
    const anyIssue = consolidated.some(c => c.olsSt === 'penetrated' || c.braSt === 'penetrated' || c.pansSt === 'penetrated' || c.rmacSt === 'penetrated' || c.msaSt === 'penetrated' || c.cirSt === 'penetrated');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg1, position: 'relative' }}>
            {downloading && (
                <div role="status" aria-live="polite" aria-label="Generating PDF" style={{ position: 'absolute', inset: 0, zIndex: 9999, background: 'rgba(7,17,30,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    <div style={{ position: 'relative', width: 80, height: 80 }}>
                        <div aria-hidden="true" style={{ width: 80, height: 80, border: `5px solid ${T.border}`, borderTop: `5px solid ${T.blue}`, borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: T.white }}>
                            {downloadProgress}%
                        </div>
                    </div>
                    <div role="progressbar" aria-valuenow={downloadProgress} aria-valuemin={0} aria-valuemax={100} style={{ width: 220, height: 4, background: T.border, borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${downloadProgress}%`, height: '100%', background: T.blue, borderRadius: 4, transition: 'width 0.3s ease' }} />
                    </div>
                    <div style={{ color: T.white, fontSize: 14, fontWeight: 700 }}>Generating PDF…</div>
                    <div style={{ color: T.muted, fontSize: 11 }}>Rendering all pages — this may take a few seconds</div>
                </div>
            )}

            <header className="no-print" style={{ flexShrink: 0, background: T.bg1, borderBottom: `1px solid ${T.border}`, padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.white }}><span aria-hidden="true">📄</span> Report Preview</span>
                {finalized && (
                    <span role="status" style={{ fontSize: 10, background: '#2ec98b22', border: '1px solid #2ec98b55', color: '#2ec98b', borderRadius: 4, padding: '2px 8px', fontWeight: 700 }}>FINALIZED</span>
                )}
                <div style={{ flex: 1 }} />
                {!finalized ? (
                    <button onClick={() => { setFinalized(true); try { localStorage.setItem(`report_finalized_${project?.id}`, 'true'); } catch {} }} style={{ padding: '7px 18px', borderRadius: 8, border: '1px solid #2ec98b66', background: 'rgba(46,201,139,0.13)', color: '#2ec98b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        <span aria-hidden="true">✅</span> Finalize Report
                    </button>
                ) : (
                    <button onClick={() => { setFinalized(false); try { localStorage.removeItem(`report_finalized_${project?.id}`); } catch {} }} style={{ padding: '7px 18px', borderRadius: 8, border: `1px solid ${T.border}`, background: 'rgba(232,64,64,0.1)', color: '#e84040', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        <span aria-hidden="true">🔓</span> UnFinalize
                    </button>
                )}
                <button onClick={downloadReport} disabled={downloading} aria-label={downloading ? 'Generating PDF' : 'Download report as PDF'} style={{ padding: '7px 18px', borderRadius: 8, border: `1px solid ${T.blue}55`, background: 'rgba(58,138,196,0.15)', color: T.blue, fontSize: 12, fontWeight: 700, cursor: downloading ? 'wait' : 'pointer', opacity: downloading ? 0.7 : 1 }}>
                    {downloading ? <><span aria-hidden="true">⏳</span> Generating PDF…</> : <><span aria-hidden="true">⬇</span> Download PDF</>}
                </button>
                <button onClick={downloadDocx} disabled={downloadingDocx} aria-label={downloadingDocx ? 'Generating Word' : 'Download report as Word'} style={{ padding: '7px 18px', borderRadius: 8, border: `1px solid #7c3aed55`, background: 'rgba(124,58,237,0.12)', color: '#a78bfa', fontSize: 12, fontWeight: 700, cursor: downloadingDocx ? 'wait' : 'pointer', opacity: downloadingDocx ? 0.7 : 1 }}>
                    {downloadingDocx ? <><span aria-hidden="true">⏳</span> Generating…</> : <><span aria-hidden="true">📝</span> Download Word</>}
                </button>
            </header>

            <main id="report-preview" ref={reportRef} style={{ flex: 1, overflowY: 'auto' }}>
                {isAnnex14Only ? (
                    <AeroOlsReport project={project} assessAll={assessAll} rwyProfile={rwyProfile} />
                ) : (
                    <article style={{ maxWidth: 900, margin: '24px auto', padding: '0 24px 48px', fontFamily: 'Georgia, serif', background: T.bg0 }}>
                        {/* Cover Page */}
                    <section className="cover-page page-break" aria-label="Cover page" style={{ height: '277mm', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center', padding: '40px' }}>
                        <div style={{ marginBottom: 40 }}>
                            <img src="/reportHeaderImage.png" alt="Tsela Obstacle Safeguarding" style={{ height: 60, display: 'block', margin: '0 auto' }} />
                        </div>
                        <div style={{ fontSize: 16, color: T.muted, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 40 }}>Cover Page</div>
                        <h1 style={{ fontSize: 32, fontWeight: 900, color: T.white, marginBottom: 20, lineHeight: 1.4, textTransform: 'uppercase' }}>Client Report for the<br />Aeronautical Assessment of Aviation Obstacle</h1>
                        <div style={{ fontSize: 18, color: T.muted, marginBottom: 40, textTransform: 'uppercase' }}>Conducted on the</div>
                        <div style={{ fontSize: 26, fontWeight: 900, color: T.blue, marginBottom: 80, textTransform: 'uppercase' }}>{proj.name} - {proj.obstacle_type}</div>
                        <div style={{ flex: 1 }}></div>
                        <address style={{ textAlign: 'left', margin: '0 auto', width: 'fit-content', borderTop: `2px solid ${T.blue}`, paddingTop: 20, fontStyle: 'normal' }}>
                            <div style={{ fontWeight: 'bold', fontSize: 16 }}>Mr Graham Mondzinger</div>
                            <div style={{ fontSize: 14, color: T.muted }}>Obstacle Evaluation Specialist</div>
                            <div style={{ fontSize: 14, color: T.white }}>Tsela Obstacle Safeguarding</div>
                            <div style={{ fontSize: 14, color: T.muted, marginTop: 10 }}>Tel: +27 62 002 1621</div>
                            <div style={{ fontSize: 14, color: T.muted }}>Email: graham@obstacles.co.za</div>
                        </address>
                    </section>

                    <div className="page-break"></div>

                    {/* Executive Summary */}
                    <section aria-labelledby="sec-1">
                        <SectionTitle n="1" title="Executive Summary" id="sec-1" />
                        <p style={{ fontSize: 12, lineHeight: 1.6, color: T.white, textAlign: 'justify', marginBottom: 40 }}>
                            An Aviation Impact Assessment was conducted on the proposed <strong>{proj.name}</strong>. The details of the proposed structure/s were captured in the Obstacle Detail table in this report. The evaluation was conducted using the principles of Obstacle Limitation Surfaces (OLS) as contained in ICAO Annex 14 documentation, Obstacle Evaluation Surfaces in accordance with ICAO DOC 8168 VOL II and CNS BRA in accordance with ICAO EUR Doc 015 Third Edition. As a result of this assessment the structure is recommended for approval.
                        </p>

                        <h3 style={{ borderBottom: `1px solid ${T.border}`, paddingBottom: 8, color: T.white }}>Table of Contents</h3>
                        <ul style={{ listStyle: 'none', padding: 0, fontSize: 12, lineHeight: 2, color: T.white, marginBottom: 40 }}>
                            <li>1. Executive Summary</li>
                            <li>2. Evaluation Details</li>
                            <li>3. Project Details</li>
                            <li>4. Obstacle Details</li>
                            <li>5. Purpose</li>
                            <li>6. Obstacle Screening Parameters</li>
                            <li>7. Scope
                                <ul style={{ listStyle: 'none', paddingLeft: 20 }}>
                                    <li>7.1 Assessment Scope</li>
                                    <li>7.2 Scope Disclaimer</li>
                                </ul>
                            </li>
                            <li>8. Aerodrome Surfaces
                                <ul style={{ listStyle: 'none', paddingLeft: 20 }}>
                                    <li>8.1 Obstacle Limitation (OLS) Assessments</li>
                                    <li>8.2 Obstacle Free Zone (OFZ) Assessment</li>
                                </ul>
                            </li>
                            <li>9. Obstacle Evaluation Surfaces (OES)
                                <ul style={{ listStyle: 'none', paddingLeft: 20 }}>
                                    <li>9.1 Precision OES</li>
                                    <li>9.2 Non Precision OES</li>
                                    <li>9.3 RNAV Routes OES</li>
                                    <li>9.4 Visual Segment Surface (VSS) OES</li>
                                    <li>9.5 Visual Manoeuvring (Circling) Area OES</li>
                                    <li>9.6 Departure OES</li>
                                    <li>9.7 Radar Minimum Altitude Chart (RMAC) OES</li>
                                </ul>
                            </li>
                            <li>10. CNS Building Restricted Area (BRA)
                                <ul style={{ listStyle: 'none', paddingLeft: 20 }}>
                                    <li>10.1 Omni-Directional CNS Building Restricted Area</li>
                                </ul>
                            </li>
                            <li>11. Conclusion</li>
                            <li>12. Recommendations</li>
                        </ul>
                    </section>

                    <div className="page-break"></div>

                    {/* Evaluation Details */}
                    <section aria-labelledby="sec-2">
                        <SectionTitle n="2" title="Evaluation Details" id="sec-2" />
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, marginBottom: 14 }}>
                            <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Aerodromes evaluated in this assessment</caption>
                            <TblHead cols={['Aerodromes', 'Registration / License Status', 'Runways']} />
                            <tbody>
                                {nearby_arps.length > 0 ? nearby_arps.map((a, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                        <Td v={`${a.name} (${a.icao})`} />
                                        <Td v={a.type || 'Unknown'} />
                                        <Td v={a.runways || 'Nil'} />
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={3} style={{ padding: '10px', border: `1px solid ${T.border}`, textAlign: 'center', color: T.muted }}>Nil</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <div style={{ fontSize: 10, color: T.muted, fontStyle: 'italic', marginBottom: 14 }}>
                            Only aerodromes where obstacle assessment happened are listed here.
                        </div>
                        <PreviewMapImage projectId={proj.id} />
                    </section>

                    <div className="page-break"></div>

                    {/* Project Details */}
                    <section aria-labelledby="sec-3">
                        <SectionTitle n="3" title="Project Details" id="sec-3" />
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 32 }}>
                            <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Project metadata</caption>
                            <tbody>
                                <R label="Project Name" value={proj.name} />
                                <R label="Reference Aerodrome" value={proj.aerodrome} />
                                {proj.project_type === 'annex_14_ols_assessment' && (
                                    <>
                                        <R label="Airport Name" value={proj.airport_name} />
                                        <R label="Airport Designator" value={proj.airport_designator} />
                                        <R label="Survey Company" value={proj.survey_company} />
                                        <R label="Survey Year" value={proj.survey_year} />
                                    </>
                                )}
                                <R label="Obstacle Type" value={proj.obstacle_type} />
                                <R label="Status" value={proj.status} />
                                <R label="Description" value={proj.description} />
                            </tbody>
                        </table>

                        {proj.project_type === 'annex_14_ols_assessment' && (
                            <RunwayProfileChart
                                points={rwyProfile?.points}
                                thresholds={rwyProfile?.thresholds}
                                notAvailable={proj.rwy_profile_not_available}
                            />
                        )}
                    </section>

                    {/* Obstacle Details */}
                    <section aria-labelledby="sec-4">
                        <SectionTitle n="4" title="Obstacle Details" id="sec-4" />
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                            <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Obstacles included in the assessment</caption>
                            <TblHead cols={['#', 'Name', 'Type', 'Lat (DD)', 'Lon (DD)', 'Elev (m)', 'Elev (ft)', 'H.Tol (m)']} />
                            <tbody>
                                {obstacles.map((o, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                        <Td v={i + 1} gray />
                                        <Td v={o.name} />
                                        <Td v={o.obstacle_type} gray />
                                        <Td v={o.latitude?.toFixed(6)} />
                                        <Td v={o.longitude?.toFixed(6)} />
                                        <Td v={o.elevation_m} />
                                        <Td v={o.elevation_m ? (o.elevation_m / 0.3048).toFixed(1) : null} gray />
                                        <Td v={o.h_tolerance} gray />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <div className="page-break"></div>

                    {/* Purpose */}
                    <section aria-labelledby="sec-5">
                        <SectionTitle n="5" title="Purpose" id="sec-5" />
                        <div style={{ fontSize: 12, lineHeight: 1.6, color: T.white, textAlign: 'justify', marginBottom: 32 }}>
                            <p>The primary aim of this study is to thoroughly evaluate the potential impact of the proposed <strong>{proj.name}</strong> on local aerodromes, flight procedure designs, and Communication, Navigation, and Surveillance (CNS) systems.</p>
                            <p>This assessment will address:</p>
                            <ul style={{ marginTop: 0 }}>
                                <li><strong>Aerodromes:</strong> How the development may affect airport operations, safety, and efficiency.</li>
                                <li><strong>Flight Procedure Designs:</strong> Potential changes to in-flight procedures are required to accommodate new or altered airspace structures.</li>
                                <li><strong>CNS Equipment:</strong> The influence on existing Communication, Navigation, and Surveillance systems, including any necessary updates or adjustments.</li>
                                <li><strong>Airspace:</strong> The potential impact on airspace limits.</li>
                            </ul>
                            <p>The study will provide a detailed analysis of these impacts and offer actionable recommendations to ensure the development aligns with safety standards and operational efficiency. We aim to identify any challenges and propose solutions to facilitate a smooth integration of the proposed <strong>{proj.name}</strong> into the current aviation framework.</p>
                        </div>
                    </section>

                    {/* Obstacle Screening Parameters */}
                    <section aria-labelledby="sec-6">
                        <SectionTitle n="6" title="Obstacle Screening Parameters" id="sec-6" />
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 32 }}>
                            <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Screening buffers by evaluation type</caption>
                            <TblHead cols={['Evaluation Type', 'Distance (m)', 'Distance (NM)']} />
                            <tbody>
                                <tr><Td v="Annex 14 OLS Buffer" gray /><Td v="15,000" /><Td v="8.1" /></tr>
                                <tr><Td v="Flight Procedure Design Buffer" gray /><Td v="56,560" /><Td v="30" /></tr>
                                <tr><Td v="RMAC / RTCC Buffer" gray /><Td v="101,860" /><Td v="55" /></tr>
                                <tr><Td v="CNS Buffer" gray /><Td v="15,000" /><Td v="8.1" /></tr>
                            </tbody>
                        </table>
                    </section>

                    <div className="page-break"></div>

                    {/* Scope */}
                    <section aria-labelledby="sec-7">
                        <SectionTitle n="7" title="Scope" id="sec-7" />
                        <SubHeading>7.1 Assessment Scope</SubHeading>
                        {(() => {
                            const olsD = [...new Set(ols_results.map(r => r.aerodrome))].join(', ');
                            const fpsS = new Set([...pans_ops_results.map(r => r.aerodrome), ...ils_results.map(r => r.icao), ...msa_results.map(r => r.aerodrome), ...(circling_results || []).map(r => r.aerodrome), ...Object.keys(npaResults || {}), ...Object.keys(vssResults || {}), ...Object.keys(rnavResults || {})]);
                            const fpsD = [...fpsS].join(', ');
                            const rmdD = [...new Set(rmac_results.map(r => r.aerodrome))].join(', ');
                            const cnsD = [...new Set(bra_results.map(r => r.navaid_ident))].join(', ');

                            return (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 20 }}>
                                    <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Features assessed and where they occur</caption>
                                    <TblHead cols={['Feature', 'Occurrence', 'Details']} />
                                    <tbody>
                                        {olsD && <tr><Td v="Annex 14 OLS" /><Td v={[...new Set(ols_results.map(r => r.aerodrome))].length} /><Td v={olsD} /></tr>}
                                        {fpsD && <tr><Td v="FPS" /><Td v={fpsS.size} /><Td v={fpsD} /></tr>}
                                        {rmdD && <tr><Td v="RMAC / RTCC" /><Td v={[...new Set(rmac_results.map(r => r.aerodrome))].length} /><Td v={rmdD} /></tr>}
                                        {cnsD && <tr><Td v="CNS Facilities" /><Td v={[...new Set(bra_results.map(r => r.navaid_ident))].length} /><Td v={cnsD} /></tr>}
                                    </tbody>
                                </table>
                            );
                        })()}

                        <SubHeading style={{ marginTop: 32 }}>7.2 Scope Disclaimer</SubHeading>
                        <div style={{ fontSize: 11, lineHeight: 1.6, color: T.white, textAlign: 'justify' }}>
                            <p>Kindly note that this study specifically focuses on analysing the impact of the proposed <strong>{proj.name}</strong> on aerodromes, flight procedure designs, and Communication, Navigation, and Surveillance (CNS) equipment. The scope of this study includes:</p>
                            <ul style={{ marginTop: 0 }}>
                                <li>Assessment of potential effects on local aerodromes.</li>
                                <li>Evaluation of changes needed in flight procedure designs.</li>
                                <li>Examination of impacts on CNS systems and necessary adjustments.</li>
                            </ul>
                            <p>Any aspects or issues not explicitly mentioned in the scope of this study will not be covered. This includes but is not limited to impacts on non-aviation infrastructure, environmental considerations outside the immediate scope, and broader regional planning concerns. For a comprehensive analysis of these additional areas, separate studies or assessments may be required.</p>
                        </div>
                    </section>

                    <div className="page-break"></div>

                    {/* Aerodrome Surfaces */}
                    <section aria-labelledby="sec-8">
                        <SectionTitle n="8" title="Aerodrome Surfaces" id="sec-8" />
                        {(() => {
                            const OLS_SURFS = new Set(['Approach', 'Transitional', 'Strip', 'IHS', 'Conical', 'OHS', 'Take-off Climb']);
                            const OFZ_SURFS = new Set(['Inner Approach', 'Balked Landing', 'Inner Transitional']);

                            const obsLookup = {};
                            obstacles.forEach(o => { obsLookup[o.id] = o; });

                            const simpleSurface = (name) => {
                                if (!name) return name;
                                if (name.startsWith('Approach')) return 'Approach';
                                if (name.startsWith('Transitional')) return 'Transitional';
                                if (name.startsWith('Strip')) return 'Strip';
                                if (name.startsWith('Inner Horizontal')) return 'IHS';
                                if (name.startsWith('Conical')) return 'Conical';
                                if (name.startsWith('Outer Horizontal')) return 'OHS';
                                if (name.startsWith('Take-off')) return 'Take-off Climb';
                                if (name.startsWith('Departure')) return 'Take-off Climb';
                                if (name.startsWith('Inner Approach')) return 'Inner Approach';
                                if (name.startsWith('Balked')) return 'Balked Landing';
                                if (name.startsWith('Inner Transitional')) return 'Inner Transitional';
                                return name;
                            };

                            // Strip is tied to a specific runway pair (like Approach/Transitional),
                            // not aerodrome-wide — only IHS/Conical/OHS are omni.
                            const OMNI = new Set(['IHS', 'Conical', 'OHS']);
                            const byAerodrome = {};
                            ols_results.forEach(r => {
                                if (!byAerodrome[r.aerodrome]) byAerodrome[r.aerodrome] = { name: r.aerodrome_name, results: [] };
                                byAerodrome[r.aerodrome].results.push(r);
                            });

                            const renderAeroSection = (icao, name, results, surfaceSet, sectionType) => {
                                const rows = [];
                                const seen = new Set();
                                results.forEach(r => {
                                    const obs = obsLookup[r.obstacle_id];
                                    if (!obs) return;
                                    (r.surfaces || []).forEach(s => {
                                        const surfName = simpleSurface(s.name);
                                        if (!surfaceSet.has(surfName)) return;
                                        const isOmni = OMNI.has(surfName);
                                        const key = isOmni ? `${r.obstacle_id}-${surfName}` : `${r.obstacle_id}-${surfName}-${s.rwy}`;
                                        if (seen.has(key)) return;
                                        seen.add(key);
                                        const surfElev = s.surf_elev_amsl != null ? s.surf_elev_amsl : null;
                                        const penetration = s.margin != null ? parseFloat((-s.margin).toFixed(3)) : null;
                                        // Strip has no elevation/margin (any obstacle inside it is an
                                        // automatic impact — see _assess_obstacle_ols_geometric), so
                                        // isPenetrated must come from status, not a null penetration value.
                                        const isPenetrated = s.status === 'penetrated';
                                        rows.push({
                                            obsName: r.obstacle_name,
                                            lat: obs.latitude,
                                            lon: obs.longitude,
                                            elev: obs.elevation_m,
                                            surface: surfName,
                                            surfElev,
                                            penetration,
                                            status: s.status,
                                            rwy: isOmni ? '—' : (s.rwy || '—'),
                                            isPenetrated
                                        });
                                    });
                                });

                                if (rows.length === 0) return null;

                                const sectionKey = `${sectionType}_${icao}`;
                                const isExcl = excludedSections.has(sectionKey);

                                return (
                                    <div key={`${icao}-${sectionType}`} style={{ marginBottom: 36, pageBreakInside: 'avoid' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                            <SubHeading level={4} style={{ letterSpacing: 0.4, textTransform: 'uppercase', margin: 0 }}>
                                                {icao}{name ? ` – ${name}` : ''} ({sectionType.toUpperCase()})
                                            </SubHeading>
                                            <ExcludeBtn sectionKey={sectionKey} label={`${icao} ${sectionType.toUpperCase()}`} />
                                        </div>
                                        {!isExcl && (
                                            <>
                                                <OlsMapImage icao={icao} type={sectionType} projectId={proj.id} />
                                                <SortableTable
                                                    caption={`${sectionType.toUpperCase()} results for ${icao}`}
                                                    headers={['Obstacle ID', 'Latitude', 'Longitude', 'Elevation (m)', 'Surface', 'Surface Elevation (m)', 'Penetration (m)', 'RWY', 'Comments']}
                                                    rows={rows.map(row => ({
                                                        isPenetrated: row.isPenetrated,
                                                        sortKeys: [row.obsName, row.lat, row.lon, row.elev, row.surface, row.surfElev, row.penetration, row.rwy],
                                                        cells: [
                                                            row.obsName,
                                                            toDMS(row.lat, true),
                                                            toDMS(row.lon, false),
                                                            row.elev,
                                                            row.surface,
                                                            row.surfElev ?? '—',
                                                            <span style={{ color: row.penetration == null ? T.muted : row.penetration > 0 ? '#e84040' : T.safe, fontWeight: 700 }}>
                                                                {row.isPenetrated && <span style={{ position: 'absolute', left: -10000 }}>Penetration: </span>}
                                                                {row.penetration != null ? row.penetration.toFixed(2) : '—'}
                                                            </span>,
                                                            row.rwy,
                                                            penetrationComment(row.isPenetrated, row.penetration)
                                                        ]
                                                    }))}
                                                    filename={`${sectionType.toUpperCase()}_${icao}.csv`}
                                                />
                                            </>
                                        )}
                                    </div>
                                );
                            };

                            const olsIcaos = Object.keys(byAerodrome).sort();

                            return (
                                <>
                                    <SubHeading level={3}>8.1 Obstacle Limitation (OLS) Assessments</SubHeading>
                                    {ols_results.length === 0 ? (
                                        <div style={{ padding: 14, textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}>No obstacles found under the Obstacle Limitation (OLS) surfaces</div>
                                    ) : (
                                        <>
                                            {!isPdfRender && <AeroTabs label="OLS aerodrome selection" tabs={olsIcaos} active={olsIcaos[olsTab]} onChange={(tab) => setOlsTab(olsIcaos.indexOf(tab))} />}
                                            {olsIcaos.map((icao, idx) => {
                                                if (!isPdfRender && idx !== olsTab) return null;
                                                return renderAeroSection(icao, byAerodrome[icao].name, byAerodrome[icao].results, OLS_SURFS, 'ols');
                                            })}
                                        </>
                                    )}

                                    <SubHeading level={3}>8.2 Obstacle Free Zone (OFZ) Assessment</SubHeading>
                                    {(() => {
                                        if (ols_results.length === 0) return <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}><tbody><tr><td style={{ padding: '16px', textAlign: 'center', color: T.muted, border: `1px solid ${T.border}` }}>No obstacles detected in the OFZ</td></tr></tbody></table>;

                                        const renderedTabs = [];
                                        olsIcaos.forEach(icao => {
                                            const res = renderAeroSection(icao, byAerodrome[icao].name, byAerodrome[icao].results, OFZ_SURFS, 'ofz');
                                            if (res) renderedTabs.push({ icao, element: res });
                                        });
                                        const availableIcaos = renderedTabs.map(t => t.icao);

                                        if (renderedTabs.length === 0) {
                                            return (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                                    <tbody><tr><td style={{ padding: '16px', textAlign: 'center', color: T.muted, border: `1px solid ${T.border}` }}>No obstacles detected in the OFZ</td></tr></tbody>
                                                </table>
                                            );
                                        }

                                        const safeOfzTabIdx = ofzTab < availableIcaos.length ? ofzTab : 0;

                                        return (
                                            <>
                                                {!isPdfRender && <AeroTabs label="OFZ aerodrome selection" tabs={availableIcaos} active={availableIcaos[safeOfzTabIdx]} onChange={(tab) => setOfzTab(availableIcaos.indexOf(tab))} />}
                                                {renderedTabs.map((item, idx) => {
                                                    if (!isPdfRender && idx !== safeOfzTabIdx) return null;
                                                    return item.element;
                                                })}
                                            </>
                                        );
                                    })()}
                                </>
                            );
                        })()}

                        {/* 8.3 OLS (ADG) — Amendment 18 Aeroplane Design Group surfaces */}
                        <SubHeading level={3}>8.3 Obstacle Limitation Surfaces — ADG (Amendment 18)</SubHeading>
                        {(() => {
                            const adgGroups = (assessAll.ols_adg_results || []).filter(g => (g.results || []).length > 0);
                            if (adgGroups.length === 0) {
                                return <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}><tbody><tr><td style={{ padding: '16px', textAlign: 'center', color: T.muted, border: `1px solid ${T.border}` }}>No OLS (ADG) results yet — open the OLS ADG tab and assess an aerodrome, or run the project-wide assessment.</td></tr></tbody></table>;
                            }
                            const adgIcaos = adgGroups.map(g => g.aerodrome).sort();
                            const safeAdgTabIdx = olsAdgTab < adgIcaos.length ? olsAdgTab : 0;
                            const adgObsLookup = {};
                            obstacles.forEach(o => { adgObsLookup[o.id] = o; });

                            const renderAdgSection = (icao) => {
                                const group = adgGroups.find(g => g.aerodrome === icao);
                                const rows = (group?.results || []).filter(r => r.status !== 'outside');
                                const sectionKey = `ols_adg_${icao}`;
                                const isExcl = excludedSections.has(sectionKey);

                                return (
                                    <div key={icao} style={{ marginBottom: 36, pageBreakInside: 'avoid' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                            <SubHeading level={4} style={{ letterSpacing: 0.4, textTransform: 'uppercase', margin: 0 }}>
                                                {icao} (OLS ADG)
                                            </SubHeading>
                                            <ExcludeBtn sectionKey={sectionKey} label={`${icao} OLS ADG`} />
                                        </div>
                                        {!isExcl && (
                                            rows.length === 0 ? (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}><tbody><tr><td style={{ padding: '16px', textAlign: 'center', color: T.muted, border: `1px solid ${T.border}` }}>No obstacles found under {icao}'s OLS (ADG) surfaces</td></tr></tbody></table>
                                            ) : (
                                                <SortableTable
                                                    caption={`OLS (ADG) results for ${icao}`}
                                                    headers={['Obstacle ID', 'Latitude', 'Longitude', 'Elevation (m)', 'Surface', 'Surface Elevation (m)', 'Penetration (m)', 'RWY', 'Comments']}
                                                    rows={rows.map(row => {
                                                        const isPenetrated = row.status === 'penetrated';
                                                        const obs = adgObsLookup[row.obstacle_id];
                                                        const penetration = row.margin != null ? parseFloat((-row.margin).toFixed(3)) : null;
                                                        return {
                                                            isPenetrated,
                                                            sortKeys: [row.obstacle_name, obs?.latitude, obs?.longitude, obs?.elevation_m, row.surface, row.surf_elev_amsl, penetration, row.runway],
                                                            cells: [
                                                                row.obstacle_name,
                                                                toDMS(obs?.latitude, true),
                                                                toDMS(obs?.longitude, false),
                                                                obs?.elevation_m ?? '—',
                                                                row.surface || '—',
                                                                row.surf_elev_amsl != null ? row.surf_elev_amsl.toFixed(2) : '—',
                                                                <span style={{ color: penetration == null ? T.muted : penetration > 0 ? '#e84040' : T.safe, fontWeight: 700 }}>
                                                                    {isPenetrated && <span style={{ position: 'absolute', left: -10000 }}>Penetration: </span>}
                                                                    {penetration != null ? penetration.toFixed(2) : '—'}
                                                                </span>,
                                                                row.runway || '—',
                                                                penetrationComment(isPenetrated, penetration),
                                                            ]
                                                        };
                                                    })}
                                                    filename={`OLS_ADG_${icao}.csv`}
                                                />
                                            )
                                        )}
                                    </div>
                                );
                            };

                            return (
                                <>
                                    {!isPdfRender && <AeroTabs label="OLS (ADG) aerodrome selection" tabs={adgIcaos} active={adgIcaos[safeAdgTabIdx]} onChange={(tab) => setOlsAdgTab(adgIcaos.indexOf(tab))} />}
                                    {adgIcaos.map((icao, idx) => {
                                        if (!isPdfRender && idx !== safeAdgTabIdx) return null;
                                        return renderAdgSection(icao);
                                    })}
                                </>
                            );
                        })()}
                    </section>

                    <div className="page-break"></div>

                    {/* Obstacle Evaluation Surfaces (OES) */}
                    <section aria-labelledby="sec-9">
                        <SectionTitle n="9" title="Obstacle Evaluation Surfaces (OES)" id="sec-9" />

                        {/* 9.1 Precision OES */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <SubHeading level={3}>9.1 Precision OES</SubHeading>
                            <ExcludeBtn sectionKey="oes_precision" label="Precision OES" />
                        </div>
                        {!excludedSections.has('oes_precision') && (
                            ils_results.length === 0 ? (
                                <div style={{ padding: 14, textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}>No obstacles found under the Precision OES (ILS) surfaces</div>
                            ) : (
                                ils_results.map((aero, ai) => {
                                    const aeroKey = `ils_${aero.icao}`;
                                    const isAeroExcl = excludedSections.has(aeroKey);
                                    return (
                                        <div key={ai} style={{ marginBottom: 32 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.border}`, paddingBottom: 4, marginBottom: 12 }}>
                                                <SubHeading level={4} style={{ letterSpacing: 0.4, textTransform: 'uppercase', margin: 0 }}>
                                                    {aero.icao}{aero.aerodrome_name ? ` – ${aero.aerodrome_name}` : ''}
                                                </SubHeading>
                                                <ExcludeBtn sectionKey={aeroKey} label={`${aero.icao} Precision OES`} />
                                            </div>
                                            {!isAeroExcl && (aero.procedures || []).map((proc, pi) => {
                                                const procKey = `ils_${aero.icao}_${proc.runway}_${proc.ils_category}`;
                                                const isProcExcl = excludedSections.has(procKey);
                                                return (
                                                    <div key={pi} style={{ marginBottom: 24, paddingLeft: 12, borderLeft: `2px solid ${T.border}44`, pageBreakInside: 'avoid' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                            <SubHeading level={5} style={{ margin: 0, fontSize: 12, fontWeight: 700, color: T.blue }}>
                                                                RWY {proc.runway} (CAT {proc.ils_category})
                                                            </SubHeading>
                                                            <ExcludeBtn sectionKey={procKey} label={`${aero.icao} RWY ${proc.runway} CAT ${proc.ils_category}`} />
                                                        </div>
                                                        {!isProcExcl && (
                                                            <>
                                                                <PansIlsMapImage icao={aero.icao} runway={proc.runway} category={proc.ils_category} projectId={proj.id} />
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                                                    <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>{`Precision ILS results for ${aero.icao} RWY ${proc.runway} CAT ${proc.ils_category}`}</caption>
                                                                    <TblHead cols={['Obstacle Name/ID', 'Obstacle Elevation (m)', 'Surface Name', 'Surface Elevation (m)', 'Penetration (m)', 'Comment']} />
                                                                    <tbody>
                                                                        {proc.results && proc.results.length > 0 ? (
                                                                            proc.results.map((r, ri) => {
                                                                                const isPen = r.penetration > 0;
                                                                                return (
                                                                                    <tr key={ri} style={{ background: isPen ? 'rgba(232,64,64,0.08)' : (ri % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent') }}>
                                                                                        <Td v={r.obstacle_name} />
                                                                                        <Td v={r.obstacle_elevation?.toFixed(2)} />
                                                                                        <Td v={r.surface_name} />
                                                                                        <Td v={r.surface_elevation?.toFixed(2)} />
                                                                                        <td style={{ padding: '5px 10px', border: `1px solid ${T.border}`, fontSize: 10, fontWeight: isPen ? 700 : 400, color: isPen ? '#e84040' : T.muted, textAlign: 'center' }}>
                                                                                            {isPen && <span style={{ position: 'absolute', left: -10000 }}>Penetration: </span>}
                                                                                            {isPen ? `+${r.penetration?.toFixed(2)}` : '—'}
                                                                                        </td>
                                                                                        <td style={{ padding: '5px 10px', border: `1px solid ${T.border}`, fontSize: 10, color: isPen ? '#e84040' : '#2EC98B' }}>
                                                                                            {r.comment || (isPen ? 'Detailed Analysis Required' : 'No impact')}
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            <tr><td colSpan={6} style={{ padding: '20px 10px', textAlign: 'center', border: `1px solid ${T.border}`, color: T.muted }}>No obstacles detected inside the ILS surfaces</td></tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })
                            )
                        )}

                        {/* 9.2 Non Precision OES */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <SubHeading level={3}>9.2 Non Precision OES</SubHeading>
                            <ExcludeBtn sectionKey="oes_npa" label="Non-Precision OES" />
                        </div>
                        {!excludedSections.has('oes_npa') && (
                            Object.keys(npaResults).length === 0 ? (
                                <div style={{ padding: 14, textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}>No obstacles found under the Non-Precision OES (NPA) surfaces</div>
                            ) : (
                                Object.entries(npaResults).map(([icao, data]) => {
                                    const arpName = data?.aerodrome_name || icao;
                                    const padRwy = (r) => {
                                        const s = String(r || '').replace(/^RWY\s*/i, '').trim();
                                        const num = s.replace(/\D+$/, '');
                                        const suf = s.slice(num.length);
                                        return num ? String(parseInt(num, 10)).padStart(2, '0') + suf.toUpperCase() : s.padStart(2, '0');
                                    };

                                    const rectByDir = {};
                                    (data?.results || []).forEach(r => {
                                        const dir = padRwy(r.approach_direction || r.runway || '');
                                        if (!rectByDir[dir]) rectByDir[dir] = [];
                                        rectByDir[dir].push(r);
                                    });

                                    const rectCols = ['Obstacle', 'RWY', 'Elev (m)', 'V-Acc (m)', 'MOC (m)', 'Effective (m)', 'OCA/OCH (m)', 'Margin (m)', 'Status'];
                                    const renderRectSubTable = (rows, rwy) => {
                                        const hasPen = rows.some(r => r.penetrates === true);
                                        return (
                                            <div key={`rect_${rwy}`} style={{ marginBottom: 14 }}>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: hasPen ? '#e84040' : T.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                                    NPA Standard Surface — RWY {rwy} <span aria-hidden="true">{hasPen ? '⚠' : '✓'}</span>
                                                    <span style={{ position: 'absolute', left: -10000 }}>{hasPen ? '(penetration detected)' : '(clear)'}</span>
                                                </div>
                                                {rows.length === 0 ? (
                                                    <div style={{ padding: '8px 12px', fontSize: 10, color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}><span aria-hidden="true">✓</span> No obstacles within NPA standard surfaces</div>
                                                ) : (
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                                                        <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>{`NPA standard surface obstacle results for runway ${rwy}`}</caption>
                                                        <thead style={{ background: 'rgba(58,138,196,0.08)' }}>
                                                            <tr>{rectCols.map(h => <th key={h} scope="col" style={{ padding: '5px 5px', color: T.blue, border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{h}</th>)}</tr>
                                                        </thead>
                                                        <tbody>
                                                            {rows.map((r, ri) => {
                                                                const isPen = r.penetrates === true;
                                                                return (
                                                                    <tr key={ri} style={{ background: isPen ? 'rgba(232,64,64,0.08)' : (ri % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent') }}>
                                                                        <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9 }}>{r.obstacle_name}</td>
                                                                        <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{padRwy(r.approach_direction || r.runway || rwy)}</td>
                                                                        <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{r.obstacle_elev_m ?? '—'}</td>
                                                                        <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{r.v_tolerance ?? r.v_tolerance_m ?? '—'}</td>
                                                                        <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>75</td>
                                                                        <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{r.effective_m ?? '—'}</td>
                                                                        <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{r.oca_m ?? '—'}</td>
                                                                        <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center', fontWeight: isPen ? 700 : 400, color: isPen ? '#e84040' : (r.oca_m != null ? '#2ec98b' : T.muted) }}>
                                                                            {r.oca_m != null ? (r.oca_m - r.effective_m).toFixed(1) : '—'}
                                                                        </td>
                                                                        <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center', fontWeight: 700, color: isPen ? '#e84040' : (isPen === false ? '#2ec98b' : T.muted) }}>
                                                                            {isPen === true ? 'PENETRATED' : isPen === false ? 'CLEAR' : 'N/A'}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>
                                        );
                                    };

                                    const npaAeroKey = `npa_${icao}`;
                                    const isAeroExcl = excludedSections.has(npaAeroKey);

                                    return (
                                        <div key={icao} style={{ marginBottom: 36 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.border}`, paddingBottom: 4, marginBottom: 12 }}>
                                                <SubHeading level={4} style={{ letterSpacing: 0.4, textTransform: 'uppercase', margin: 0 }}>
                                                    {icao} — {arpName}
                                                </SubHeading>
                                                <ExcludeBtn sectionKey={npaAeroKey} label={`${icao} Non-Precision OES`} />
                                            </div>
                                            {!isAeroExcl && (
                                                Object.keys(rectByDir).length === 0 ? (
                                                    <div style={{ padding: '14px', textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}><span aria-hidden="true">✓</span> No obstacles detected in NPA standard surfaces</div>
                                                ) : (
                                                    Object.entries(rectByDir).map(([rwy, rwyRows]) => {
                                                        const rwyKey = `npa_${icao}_rwy_${rwy}`;
                                                        const isRwyExcl = excludedSections.has(rwyKey);
                                                        const anyPen = rwyRows.some(r => r.penetrates === true);
                                                        return (
                                                            <div key={rwy} style={{ marginBottom: 28, paddingLeft: 12, borderLeft: `2px solid ${T.border}44`, pageBreakInside: 'avoid' }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                                    <h5 style={{ fontSize: 12, fontWeight: 800, color: anyPen ? '#e84040' : '#2ec98b', margin: 0, padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderLeft: `4px solid ${anyPen ? '#e84040' : '#2ec98b'}`, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                                                                        RWY {rwy} — {anyPen ? <><span aria-hidden="true">⚠</span> Penetration Detected</> : <><span aria-hidden="true">✓</span> Clear</>}
                                                                    </h5>
                                                                    <ExcludeBtn sectionKey={rwyKey} label={`${icao} RWY ${rwy}`} />
                                                                </div>
                                                                {!isRwyExcl && (
                                                                    <>
                                                                        <NpaRwyMapImage icao={icao} rwy={rwy} projectId={proj.id} />
                                                                        {renderRectSubTable(rwyRows, rwy)}
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                )
                                            )}
                                        </div>
                                    );
                                })
                            )
                        )}

                        {/* 9.3 RNAV Routes OES */}
                        <div className="page-break"></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <SubHeading level={3}>9.3 RNAV Routes OES</SubHeading>
                            <ExcludeBtn sectionKey="oes_rnav" label="RNAV OES" />
                        </div>
                        {!excludedSections.has('oes_rnav') && (() => {
                            const rnavArr = Array.isArray(rnavResults) ? rnavResults : [];
                            if (rnavArr.length === 0) {
                                return (
                                    <div key="rnav-empty" style={{ padding: 14, textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}>No obstacles found under the RNAV Routes OES surfaces</div>
                                );
                            }

                            return (
                                <div style={{ marginBottom: 24 }}>
                                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 12 }}>
                                        ATS RNAV routes within 5.77 NM of project obstacles were assessed.
                                        Obstacles inside each route's 5.77 NM buffer were evaluated to calculate the minimum enroute altitude (MEA) and MOCA.
                                    </div>
                                    
                                    <RnavMapImage icao="rnav_routes" projectId={proj.id} />
                                    
                                    {rnavArr.map((r, ri) => (
                                        <div key={ri} style={{ marginBottom: 24, padding: 14, border: `1px solid ${T.border}`, borderRadius: 6, background: 'rgba(255,255,255,0.01)', pageBreakInside: 'avoid' }}>
                                            <SubHeading level={4} style={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'flex', justifyContent: 'space-between' }}>
                                                <span>Route: {r.route_name}</span>
                                                <span style={{ fontSize: 10, color: T.blue }}>{r.obstacles_in_buffer?.length} Obstacles in Buffer</span>
                                            </SubHeading>

                                            <div style={{ margin: '12px 0', padding: 10, background: 'rgba(0, 225, 255, 0.03)', borderLeft: `3px solid #00e1ff`, borderRadius: '0 4px 4px 0' }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: T.white, fontFamily: 'monospace', marginBottom: 4 }}>
                                                    {r.report_text_moca}
                                                </div>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: T.white, fontFamily: 'monospace' }}>
                                                    {r.report_text_mea}
                                                </div>
                                            </div>

                                            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, marginBottom: 6, textTransform: 'uppercase' }}>
                                                Obstacles in Route Buffer (5.77 NM)
                                            </div>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                                                <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>{`Obstacle evaluation table for route ${r.route_name}`}</caption>
                                                <thead style={{ background: 'rgba(58,138,196,0.08)' }}>
                                                    <tr>
                                                        {['Obstacle', 'Distance', 'Elev (m)', 'V-Acc (m)', 'MOC (m)', 'MOCA (ft)', 'MOCA (m)', 'Status'].map(h => (
                                                            <th key={h} scope="col" style={{ padding: '5px 5px', color: T.blue, border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {r.obstacles_in_buffer.map((obs, idx) => {
                                                        const isControlling = obs.moca_m === r.controlling_moca_m;
                                                        return (
                                                            <tr key={idx} style={{ background: isControlling ? 'rgba(0, 225, 255, 0.05)' : idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                                                <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, fontWeight: isControlling ? 700 : 400 }}>
                                                                    {obs.name} {isControlling && <span style={{ color: '#00e1ff', fontSize: 8 }}>[Controlling]</span>}
                                                                </td>
                                                                <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>
                                                                    {obs.distance_nm.toFixed(2)} NM ({Math.round(obs.distance_m)} m)
                                                                </td>
                                                                <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{obs.elevation_m.toFixed(1)}</td>
                                                                <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{obs.v_tolerance.toFixed(1)}</td>
                                                                <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>300</td>
                                                                <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center', fontWeight: 700, color: '#00e1ff' }}>{Math.round(obs.moca_ft)}</td>
                                                                <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center' }}>{obs.moca_m.toFixed(1)}</td>
                                                                <td style={{ padding: '4px 5px', border: `1px solid ${T.border}`, fontSize: 9, textAlign: 'center', fontWeight: 700, color: '#ff5f5f' }}>IN BUFFER</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}

                        {/* 9.4 Visual Segment Surface (VSS) OES */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <SubHeading level={3}>9.4 Visual Segment Surface (VSS) OES</SubHeading>
                            <ExcludeBtn sectionKey="oes_vss" label="VSS OES" />
                        </div>
                        {!excludedSections.has('oes_vss') && (
                            Object.keys(vssResults).length === 0 ? (
                                <div style={{ padding: 14, textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}>No obstacles found under the Visual Segment Surface (VSS)</div>
                            ) : (
                                Object.entries(vssResults).map(([icao, data]) => {
                                    const rawResults = data?.results || [];
                                    const results = rawResults.filter(r => !r.ignored);
                                    const arpName = data?.aerodrome_name || icao;
                                    const vssKey = `vss_${icao}`;
                                    const isExcl = excludedSections.has(vssKey);
                                    return (
                                        <div key={icao} style={{ marginBottom: 36, pageBreakInside: 'avoid' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                <SubHeading level={4} style={{ letterSpacing: 0.4, textTransform: 'uppercase', margin: 0 }}>
                                                    {icao} - {arpName}
                                                </SubHeading>
                                                <ExcludeBtn sectionKey={vssKey} label={`${icao} VSS`} />
                                            </div>
                                            {!isExcl && (
                                                <>
                                                    <VssMapImage icao={icao} projectId={proj.id} />
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                                <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>{`VSS results for ${icao}`}</caption>
                                                <TblHead cols={['Obstacle Name/ID', 'Obstacle Elevation (m)', 'Surface Elevation (m)', 'Penetration (m)', 'Comment']} />
                                                <tbody>
                                                    {results.length === 0 ? (
                                                        <tr><td colSpan={5} style={{ padding: '20px 10px', textAlign: 'center', border: `1px solid ${T.border}`, color: T.muted }}>No obstacles detected in the VSS</td></tr>
                                                    ) : (
                                                        results.map((r, ri) => {
                                                            const isPen = r.penetrates === true;
                                                            return (
                                                                <tr key={ri} style={{ background: isPen ? 'rgba(232,64,64,0.08)' : (ri % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent') }}>
                                                                    <Td v={r.obstacle_name} />
                                                                    <Td v={r.obstacle_elev_m} />
                                                                    <Td v={r.vss_elev_m ?? '—'} />
                                                                    <td style={{ padding: '5px 10px', border: `1px solid ${T.border}`, fontSize: 10, fontWeight: isPen ? 700 : 400, color: isPen ? '#e84040' : T.muted, textAlign: 'center' }}>
                                                                        {isPen && <span style={{ position: 'absolute', left: -10000 }}>Penetration: </span>}
                                                                        {isPen ? `+${r.penetration_m}` : '—'}
                                                                    </td>
                                                                    <td style={{ padding: '5px 10px', border: `1px solid ${T.border}`, fontSize: 10, color: isPen ? '#e84040' : '#2EC98B' }}>
                                                                        {isPen ? 'Detailed Analysis Required' : 'No impact'}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                            </>
                                        )}
                                        </div>
                                    );
                                })
                            )
                        )}

                        {/* 9.5 Visual Manoeuvring (Circling) Area OES */}
                        <div className="page-break"></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <SubHeading level={3}>9.5 Visual Manoeuvring (Circling) Area OES</SubHeading>
                            <ExcludeBtn sectionKey="oes_circling" label="Circling OES" />
                        </div>
                        {!excludedSections.has('oes_circling') && (
                            circling_results.length === 0 ? (
                                <div style={{ padding: 14, textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}>No obstacles found under the Visual Manoeuvring (Circling) Area surfaces</div>
                            ) : (
                                circling_results.map((res, i) => {
                                    const getMoca = (obsName, mocFt) => {
                                        const obs = obstacles.find(o => o.name === obsName);
                                        const vTol = obs?.v_tolerance || 0;
                                        const elevM = obs?.elevation_m || 0;
                                        const baseFt = (elevM + vTol) * 3.28084;
                                        return Math.ceil((baseFt + mocFt) / 10) * 10;
                                    };

                                    const catAMin = res.category_rules ? res.category_rules['A'] || 4240 : 4240;
                                    const catBMin = res.category_rules ? res.category_rules['B'] || 4240 : 4240;
                                    const catCMin = res.category_rules ? res.category_rules['C'] || 5020 : 5020;
                                    const catDMin = res.category_rules ? res.category_rules['D'] || 5020 : 5020;

                                    const circlingKey = `circling_${res.aerodrome}`;
                                    const isExcl = excludedSections.has(circlingKey);
                                    return (
                                        <div key={i} style={{ marginBottom: 36, pageBreakInside: 'avoid' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                <SubHeading level={4} style={{ letterSpacing: 0.4, textTransform: 'uppercase', margin: 0 }}>
                                                    {res.aerodrome} - Circling Area
                                                </SubHeading>
                                                <ExcludeBtn sectionKey={circlingKey} label={`${res.aerodrome} Circling`} />
                                            </div>
                                            {!isExcl && (
                                                <>

                                            <CirclingMapImage icao={res.aerodrome} projectId={proj.id} />

                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, marginBottom: 10 }}>
                                                <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Aerodrome elevation and MOC values</caption>
                                                <thead><tr style={{ background: 'rgba(58,138,196,0.12)' }}>
                                                    <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>AD Elev (m)</th>
                                                    <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>CAT A/B MOC (ft)</th>
                                                    <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>CAT C/D MOC (ft)</th>
                                                </tr></thead>
                                                <tbody><tr>
                                                    <Td v={res.ad_elev_m != null ? res.ad_elev_m.toFixed(0) : '—'} />
                                                    <Td v={295} />
                                                    <Td v={394} />
                                                </tr></tbody>
                                            </table>

                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, marginBottom: 10 }}>
                                                <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Category minima by aircraft category</caption>
                                                <thead><tr style={{ background: 'rgba(58,138,196,0.12)' }}>
                                                    <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}`, textAlign: 'left' }}>Category</th>
                                                    <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>A</th>
                                                    <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>B</th>
                                                    <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>C</th>
                                                    <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>D</th>
                                                </tr></thead>
                                                <tbody><tr>
                                                    <Td v="Category Minima (ft)" gray />
                                                    <Td v={catAMin} />
                                                    <Td v={catBMin} />
                                                    <Td v={catCMin} />
                                                    <Td v={catDMin} />
                                                </tr></tbody>
                                            </table>

                                            <strong style={{ fontSize: 11, color: T.white, display: 'block', marginBottom: 6 }}>Results</strong>
                                            {res.results && res.results.length > 0 ? (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                                    <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Circling area obstacle results</caption>
                                                    <thead><tr style={{ background: 'rgba(58,138,196,0.12)' }}>
                                                        <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}`, textAlign: 'left' }}>Obstacle Name/ID</th>
                                                        <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>Elevation (m)</th>
                                                        <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>CAT A/B MOCA (ft)</th>
                                                        <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}` }}>CAT C/D MOCA (ft)</th>
                                                        <th scope="col" style={{ padding: 8, color: T.blue, border: `1px solid ${T.border}`, textAlign: 'left' }}>Comment</th>
                                                    </tr></thead>
                                                    <tbody>
                                                        {res.results.map((r, ri) => {
                                                            let mocaAB = getMoca(r.obstacle_name, 295);
                                                            let mocaCD = getMoca(r.obstacle_name, 394);

                                                            if (mocaAB === 0) {
                                                                const backupElev = r.obstacle_elev_m || 0;
                                                                mocaAB = Math.ceil((backupElev * 3.28084 + 295) / 10) * 10;
                                                                mocaCD = Math.ceil((backupElev * 3.28084 + 394) / 10) * 10;
                                                            }

                                                            let comment = 'Clear';
                                                            let isPen = false;

                                                            if (r.evaluations && r.evaluations.length > 0) {
                                                                const penCats = r.evaluations.filter(e => e.penetrates).map(e => e.category);
                                                                isPen = penCats.length > 0;
                                                                if (isPen) {
                                                                    if (penCats.length === 4 || (penCats.includes('A') && penCats.includes('B') && penCats.includes('C') && penCats.includes('D'))) {
                                                                        comment = 'CAT A, B, C AND D impacted.';
                                                                    } else {
                                                                        comment = `CAT ${penCats.join(', ')} impacted.`;
                                                                    }
                                                                } else if (r.ignored || r.evaluations.every(e => e.ignored)) {
                                                                    comment = 'Outside circling areas';
                                                                }
                                                            }
                                                            return (
                                                                <tr key={ri} style={{ background: isPen ? 'rgba(232,64,64,0.08)' : (ri % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent') }}>
                                                                    <Td v={r.obstacle_name} />
                                                                    <Td v={r.obstacle_elev_m?.toFixed(0)} />
                                                                    <td style={{ padding: '5px 10px', border: `1px solid ${T.border}`, fontSize: 10, textAlign: 'center', color: isPen ? '#e84040' : T.muted }}>{mocaAB}</td>
                                                                    <td style={{ padding: '5px 10px', border: `1px solid ${T.border}`, fontSize: 10, textAlign: 'center', color: isPen ? '#e84040' : T.muted }}>{mocaCD}</td>
                                                                    <td style={{ padding: '5px 10px', border: `1px solid ${T.border}`, fontSize: 10, color: isPen ? '#e84040' : '#2EC98B' }}>{comment}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <div style={{ padding: '16px', textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}>No obstacles inside circling areas</div>
                                            )}
                                            </>
                                        )}
                                        </div>
                                    );
                                })
                            )
                        )}

                        <div className="page-break"></div>
                        {/* 9.6 Departure OES */}
                        <SubHeading level={3}>9.6 Departure OES</SubHeading>
                        {(() => {
                            if (pans_ops_results.length === 0) {
                                return (
                                    <div style={{ padding: 14, textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}>No obstacles found under the Departure OIS surfaces</div>
                                );
                            }

                            const pansByIcao = {};
                            pans_ops_results.forEach(r => {
                                if (!pansByIcao[r.aerodrome]) pansByIcao[r.aerodrome] = [];
                                pansByIcao[r.aerodrome].push(r);
                            });

                            const pansIcaos = Object.keys(pansByIcao).sort();
                            const safePansTab = pansTab < pansIcaos.length ? pansTab : 0;

                            return (
                                <>
                                    {!isPdfRender && <AeroTabs label="Departure aerodrome selection" tabs={pansIcaos} active={pansIcaos[safePansTab]} onChange={(tab) => setPansTab(pansIcaos.indexOf(tab))} />}
                                    {pansIcaos.map((icao, idx) => {
                                        if (!isPdfRender && idx !== safePansTab) return null;
                                        const results = pansByIcao[icao];
                                        return (
                                            <div key={icao} style={{ marginBottom: 24 }}>
                                                <SubHeading level={4} style={{ letterSpacing: 0.4, textTransform: 'uppercase' }}>
                                                    {icao} – Departure Surface
                                                </SubHeading>
                                                <SortableTable
                                                    caption={`PANS-OPS departure results for ${icao}`}
                                                    headers={['Obstacle Name', 'Procedure', 'Clear H (m)', 'Obs AGL (m)', 'Margin (m)', 'Status']}
                                                    rows={results.map(r => {
                                                        const isPen = r.status === 'penetrated' || Number(r.margin) < 0;
                                                        return {
                                                            isPenetrated: isPen,
                                                            sortKeys: [r.obstacle_name, r.procedure, r.clearance_h, r.obs_h_agl, Number(r.margin), r.status],
                                                            cells: [
                                                                r.obstacle_name,
                                                                r.procedure,
                                                                r.clearance_h,
                                                                r.obs_h_agl,
                                                                <span style={{ color: Number(r.margin) < 0 ? T.danger : T.safe, fontWeight: 700 }}>{r.margin}</span>,
                                                                <StatusCell s={r.status} />
                                                            ]
                                                        };
                                                    })}
                                                    filename={`PansOps_Departure_${icao}.csv`}
                                                />
                                            </div>
                                        );
                                    })}
                                </>
                            );
                        })()}

                        {/* 9.7 Radar Minimum Altitude Chart (RMAC) OES */}
                        <div className="page-break"></div>
                        <SubHeading level={3}>9.7 Radar Minimum Altitude Chart (RMAC) OES</SubHeading>
                        {rmac_results.length === 0 ? (
                            <div style={{ padding: 14, textAlign: 'center', color: '#2ec98b', border: `1px solid ${T.border}`, borderRadius: 4 }}>No obstacles found under the RMAC surfaces</div>
                        ) : (
                            rmac_results.map((res, i) => {
                                const rmacKey = `rmac_${res.aerodrome}`;
                                const isExcl = excludedSections.has(rmacKey);
                                return (
                                    <div key={i} style={{ marginBottom: 36, pageBreakInside: 'avoid' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                            <SubHeading level={4} style={{ letterSpacing: 0.4, textTransform: 'uppercase', margin: 0 }}>
                                                {res.aerodrome} RMAC Report
                                            </SubHeading>
                                            <ExcludeBtn sectionKey={rmacKey} label={`${res.aerodrome} RMAC`} />
                                        </div>
                                        {!isExcl && (
                                            <>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, marginBottom: 10, tableLayout: 'fixed' }}>
                                        <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>{`RMAC sector results for ${res.aerodrome}`}</caption>
                                        <thead style={{ background: 'rgba(58,138,196,0.12)' }}>
                                            <tr>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'left', width: '14%' }}>Obstacle Name</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '7%' }}>Elev (m)</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '8%', wordBreak: 'break-word' }}>Vert. Acc. (m)</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '9%', wordBreak: 'break-word' }}>Total Elev. (m)</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '8%' }}>MOCA (ft)</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '10%' }}>Sector</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '10%', wordBreak: 'break-word' }}>Sector Minima (ft)</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '9%', wordBreak: 'break-word' }}>Clearance (ft)</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '8%' }}>Brg °T</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '8%' }}>Dist (NM)</th>
                                                <th scope="col" style={{ padding: '6px 4px', color: T.blue, border: `1px solid ${T.border}`, textAlign: 'center', width: '9%' }}>Result</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {res.results && res.results.length > 0 ? res.results.map((r, i) => (
                                                <tr key={i} style={{ borderBottom: `1px solid ${T.border}33`, background: r.is_penetration ? 'rgba(232,64,64,0.08)' : (i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)') }}>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, fontWeight: 600, fontSize: 9 }}>{r.obstacle_name}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', fontSize: 9 }}>{r.elevation_m}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', fontSize: 9 }}>{r.v_tolerance}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', fontSize: 9 }}>{r.total_elevation_m}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', color: T.blue, fontWeight: 700, fontSize: 9 }}>{r.moca_ft}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', fontStyle: 'italic', color: r.sector_name ? T.white : '#888', fontSize: 9 }}>{r.sector_name || 'Outside Areas'}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', fontSize: 9 }}>{r.sector_minima_ft != null ? r.sector_minima_ft : '—'}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', fontSize: 9 }}>{r.result_diff != null ? <span style={r.result_diff > 0 ? { color: '#e84040', fontWeight: 'bold' } : {}}>{Math.abs(r.result_diff).toFixed(0)}</span> : '—'}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', fontSize: 9 }}>{r.bearing_deg != null ? `${r.bearing_deg}°` : '—'}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', fontSize: 9 }}>{r.distance_nm ?? '—'}</td>
                                                    <td style={{ padding: '5px 4px', border: `1px solid ${T.border}`, textAlign: 'center', fontWeight: 700, color: r.sector_name ? (r.is_penetration ? '#e84040' : '#2ec98b') : '#888', fontSize: 9 }}>
                                                        {r.sector_name ? (r.is_penetration ? 'PENETRATED' : 'CLEAR') : 'N/A'}
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="11" style={{ padding: '16px', textAlign: 'center', color: T.muted, border: `1px solid ${T.border}` }}>No obstacles found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>

                                    <div style={{ marginTop: 24, textAlign: 'center' }}>
                                        <img src={`${API_BASE}/rmac/image/${res.aerodrome}`} alt={`${res.aerodrome} RMAC chart`} crossOrigin="anonymous" style={{ maxWidth: '100%', width: '14cm', height: 'auto', display: 'block', margin: '0 auto', borderRadius: 4, border: `2px solid ${T.border}` }} onError={(e) => { e.target.style.display = 'none'; }} />
                                    </div>
                                    <div style={{ marginTop: 20, padding: 14, background: 'rgba(0,0,0,0.2)', borderRadius: 6, border: `2px solid ${res.is_penetration ? '#e84040' : '#2ec98b'}` }}>
                                        <div style={{ marginBottom: 12, lineHeight: 1.5, fontSize: 11, color: T.white, fontWeight: 700 }}>
                                            {res.is_penetration
                                                ? `An obstacle penetrates the ${res.aerodrome} RMAC Sector minimum altitude. Detailed analysis required.`
                                                : `All obstacles are safely accommodated in the ${res.aerodrome} RMAC sectors without penetration.`}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, fontSize: 11 }}>
                                            <div><span style={{ color: T.muted }}>Impact Level:</span> <span style={{ fontWeight: 'bold', color: res.is_penetration ? '#e84040' : T.white }}>{res.is_penetration ? 'HIGH' : 'LOW'}</span></div>
                                            <div><span style={{ color: T.muted }}>Mitigation:</span> <span style={{ fontWeight: 'bold', color: T.white }}>{res.is_penetration ? 'Adjust RMAC / Limit Obs' : 'None Required'}</span></div>
                                        </div>
                                    </div>
                                    </>
                                )}
                                </div>
                            );
                        })
                        )}
                    </section>

                    <div className="page-break"></div>
                    {/* CNS Building Restricted Area (BRA) */}
                    <section aria-labelledby="sec-10">
                        <SectionTitle n="10" title="CNS Building Restricted Area (BRA)" id="sec-10" />
                        {!isAnnex14Only ? (
                            <>
                                {(() => {
                                    const cnsGroups = {};
                                    if (bra_results) {
                                        bra_results.forEach(r => {
                                            const key = `${r.navaid_ident}_${r.navaid_type}`;
                                            if (!cnsGroups[key]) cnsGroups[key] = {
                                                ident: r.navaid_ident,
                                                type: r.navaid_type,
                                                lat: r.lat, lon: r.lon, elev_m: r.elev_m,
                                                is_dir: r.is_dir,
                                                params: r.params || {},
                                                heading_deg: r.heading_deg,
                                                obs: []
                                            };
                                            cnsGroups[key].obs.push(r);
                                        });
                                    }

                                    const omniList = Object.values(cnsGroups).filter(g => !g.is_dir);
                                    const dirList = Object.values(cnsGroups).filter(g => g.is_dir);

                                    const ThL = { padding: 6, border: `1px solid ${T.border}`, color: T.muted, width: '25%', whiteSpace: 'nowrap' };
                                    const TdL = { padding: 6, border: `1px solid ${T.border}`, width: '25%', fontWeight: 700 };

                                    const renderGrp = (grp) => {
                                        const key = grp.is_dir ? `bra_dir_${grp.ident}` : `bra_omni_${grp.ident}`;
                                        const isExcl = excludedSections.has(key);
                                        return (
                                            <div key={`${grp.ident}-${grp.type}`} style={{ marginBottom: 36, pageBreakInside: 'avoid' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                    <h5 style={{ margin: 0, fontSize: 12, fontWeight: 700, color: T.blue }}>
                                                        {grp.ident} ({grp.type})
                                                    </h5>
                                                    <ExcludeBtn sectionKey={key} label={`${grp.ident} (${grp.type})`} />
                                                </div>
                                                {!isExcl && (
                                                    <>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, marginBottom: 8 }}>
                                                            <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>{`CNS facility and BRA parameters for ${grp.ident} ${grp.type}`}</caption>
                                                            <thead>
                                                                <tr style={{ background: 'rgba(58,138,196,0.12)' }}>
                                                                    <th colSpan="2" scope="colgroup" style={{ padding: 6, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue }}>CNS Facility</th>
                                                                    <th colSpan="2" scope="colgroup" style={{ padding: 6, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue }}>BRA Parameters</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {grp.is_dir ? (
                                                                    <>
                                                                        <tr><th scope="row" style={ThL}>Ident</th><td style={TdL}>{grp.ident}</td><th scope="row" style={ThL}>A</th><td style={TdL}>{grp.params.A ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Type</th><td style={TdL}>{grp.type}</td><th scope="row" style={ThL}>b</th><td style={TdL}>{grp.params.b ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Latitude (DD)</th><td style={TdL}>{grp.lat != null ? grp.lat.toFixed(6) : '—'}</td><th scope="row" style={ThL}>h</th><td style={TdL}>{grp.params.h ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Longitude (DD)</th><td style={TdL}>{grp.lon != null ? grp.lon.toFixed(6) : '—'}</td><th scope="row" style={ThL}>r</th><td style={TdL}>{grp.params.r ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Elevation</th><td style={TdL}>{grp.elev_m ? grp.elev_m.toFixed(2) : '—'}</td><th scope="row" style={ThL}>D</th><td style={TdL}>{grp.params.D ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Runway</th><td style={TdL}>—</td><th scope="row" style={ThL}>H</th><td style={TdL}>{grp.params.H ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Rwy Bearing</th><td style={TdL}>{grp.heading_deg && grp.heading_deg !== '—' ? `${grp.heading_deg}°` : '—'}</td><th scope="row" style={ThL}>L</th><td style={TdL}>{grp.params.L ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Dist (X) to Thr</th><td style={TdL}>{grp.params.A ?? '—'}</td><th scope="row" style={ThL}>PHI</th><td style={TdL}>{grp.params.PHI ?? '—'}</td></tr>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <tr><th scope="row" style={ThL}>Ident</th><td style={TdL}>{grp.ident}</td><th scope="row" style={ThL}>Cylinder Radius (r)</th><td style={TdL}>{grp.params.r ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Type</th><td style={TdL}>{grp.type}</td><th scope="row" style={ThL}>Cone Alpha (a)</th><td style={TdL}>{grp.params.a ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Latitude (DD)</th><td style={TdL}>{grp.lat != null ? grp.lat.toFixed(6) : '—'}</td><th scope="row" style={ThL}>Cone Radius (R)</th><td style={TdL}>{grp.params.R ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Longitude (DD)</th><td style={TdL}>{grp.lon != null ? grp.lon.toFixed(6) : '—'}</td><th scope="row" style={ThL}>Cylinder Radius (j)</th><td style={TdL}>{grp.params.j ?? '—'}</td></tr>
                                                                        <tr><th scope="row" style={ThL}>Elevation</th><td style={TdL}>{grp.elev_m ? grp.elev_m.toFixed(2) : '—'}</td><th scope="row" style={ThL}>Cylinder Height (h)</th><td style={TdL}>{grp.params.h ?? '—'}</td></tr>
                                                                    </>
                                                                )}
                                                            </tbody>
                                                        </table>

                                                        <div style={{ fontSize: 11, fontWeight: 700, color: T.blue, marginBottom: 4 }}>BRA Results</div>
                                                        {(() => {
                                                            const OUTSIDE_OUTCOMES = new Set(['outside surfaces', 'N/A', 'outside']);
                                                            const insideObs = grp.obs.filter(o => !OUTSIDE_OUTCOMES.has(o.outcome) && o.surface !== 'Outside' && o.surface !== '—');
                                                            const outsideObs = grp.obs.filter(o => OUTSIDE_OUTCOMES.has(o.outcome) || o.surface === 'Outside' || o.surface === '—');

                                                            if (insideObs.length === 0) {
                                                                return (
                                                                    <div style={{ padding: '14px 10px', border: `1px solid ${T.border}`, textAlign: 'center', color: '#2ec98b', fontWeight: 700 }}>
                                                                        <span aria-hidden="true">✓</span> No obstacles detected within this navaid BRA surface
                                                                    </div>
                                                                );
                                                            }

                                                            const headers = grp.is_dir
                                                                ? ['Obstacle Name', 'BRA Surface', 'BRA Elevation', 'Obst Elevation', 'Obst Distance', 'Obst Bearing', 'Obst X', 'Obst Y', 'Infringed']
                                                                : ['Obstacle Name', 'BRA Surface', 'BRA Elevation', 'Obst Elevation', 'Obst Distance', 'Obst Bearing', 'Infringed'];

                                                            const rows = insideObs.map(o => {
                                                                const obsElev = (o.obstacle_elev ?? 0);
                                                                const vertTol = (o.vert_tol ?? 0);
                                                                const effElev = Math.ceil((obsElev + vertTol) * 10) / 10;
                                                                const isPen = o.outcome === 'penetrated' || o.outcome === 'CNS Engineer';
                                                                const infringed = <span style={{ color: isPen ? T.danger : T.safe, fontWeight: 700 }}>{isPen ? 'Yes' : 'No'}</span>;

                                                                if (grp.is_dir) {
                                                                    return {
                                                                        isPenetrated: isPen,
                                                                        sortKeys: [o.obstacle_name, o.surface, o.surface_elev, effElev, o.dist_m, o.params?.brng, o.params?.x, o.params?.y, isPen ? 'Yes' : 'No'],
                                                                        cells: [
                                                                            o.obstacle_name, o.surface, o.surface_elev, effElev, o.dist_m,
                                                                            o.params?.brng ? `${o.params.brng}°` : '—',
                                                                            o.params?.x ?? '—', o.params?.y ?? '—',
                                                                            infringed
                                                                        ]
                                                                    };
                                                                }
                                                                return {
                                                                    isPenetrated: isPen,
                                                                    sortKeys: [o.obstacle_name, o.surface, o.surface_elev, effElev, o.dist_m, o.params?.brng, isPen ? 'Yes' : 'No'],
                                                                    cells: [
                                                                        o.obstacle_name, o.surface, o.surface_elev, effElev, o.dist_m,
                                                                        o.params?.brng != null ? `${o.params.brng}°` : '—',
                                                                        infringed
                                                                    ]
                                                                };
                                                            });

                                                            return (
                                                                <>
                                                                    <SortableTable caption={`BRA results for ${grp.ident}`} headers={headers} rows={rows} filename={`BRA_${grp.ident}_${grp.type}.csv`} />
                                                                    {outsideObs.length > 0 && (
                                                                        <div style={{ padding: '6px 10px', border: `1px solid ${T.border}`, color: T.muted, fontSize: 9, textAlign: 'center', fontStyle: 'italic', marginTop: -15, marginBottom: 20 }}>
                                                                            {outsideObs.length} obstacle{outsideObs.length > 1 ? 's' : ''} ({outsideObs.map(o => o.obstacle_name).join(', ')}) fall{outsideObs.length === 1 ? 's' : ''} outside this BRA surface
                                                                        </div>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    };

                                    const safeOmniTab = braOmniTab < omniList.length ? braOmniTab : 0;
                                    const safeDirTab = braDirTab < dirList.length ? braDirTab : 0;
                                    const omniTabs = omniList.map(g => `${g.ident} (${g.type})`);
                                    const dirTabs = dirList.map(g => `${g.ident} (${g.type})`);

                                    return (
                                        <>
                                            <SubHeading level={3}>10.1 Omni-Directional CNS Building Restricted Area</SubHeading>
                                            <img src={`${API_BASE}/static/CNSOMNI.png?t=${Date.now()}`} alt="Omni-directional CNS BRA reference diagram" style={{ width: '100%', maxWidth: '20cm', height: 'auto', maxHeight: '18cm', objectFit: 'contain', borderRadius: 8, marginBottom: 14, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 14px' }} />

                                            {omniList.length > 0 ? (
                                                <>
                                                    {!isPdfRender && <AeroTabs label="Omni-directional navaid selection" tabs={omniTabs} active={omniTabs[safeOmniTab]} onChange={(tab) => setBraOmniTab(omniTabs.indexOf(tab))} />}
                                                    {omniList.map((grp, idx) => {
                                                        if (!isPdfRender && idx !== safeOmniTab) return null;
                                                        return renderGrp(grp);
                                                    })}
                                                </>
                                            ) : (
                                                <div style={{ padding: '16px', textAlign: 'center', color: T.muted, border: `1px solid ${T.border}` }}>No omni-directional navaids detected.</div>
                                            )}

                                            <SubHeading level={3} style={{ marginTop: 32 }}>10.2 Directional CNS Building Restricted Area</SubHeading>
                                            <img src={`${API_BASE}/static/CNSDIR.png?t=${Date.now()}`} alt="Directional CNS BRA reference diagram" style={{ width: '100%', maxWidth: '20cm', height: 'auto', maxHeight: '18cm', objectFit: 'contain', borderRadius: 8, marginBottom: 14, border: `1px solid ${T.border}`, display: 'block', margin: '0 auto 14px' }} />

                                            {dirList.length > 0 ? (
                                                <>
                                                    {!isPdfRender && <AeroTabs label="Directional navaid selection" tabs={dirTabs} active={dirTabs[safeDirTab]} onChange={(tab) => setBraDirTab(dirTabs.indexOf(tab))} />}
                                                    {dirList.map((grp, idx) => {
                                                        if (!isPdfRender && idx !== safeDirTab) return null;
                                                        return renderGrp(grp);
                                                    })}
                                                </>
                                            ) : (
                                                <div style={{ padding: '16px', textAlign: 'center', color: T.muted, border: `1px solid ${T.border}` }}>No directional navaids detected.</div>
                                            )}
                                        </>
                                    );
                                })()}
                            </>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, marginTop: 10 }}>
                                <tbody><tr><td style={{ padding: '16px', textAlign: 'center', color: T.muted, border: `1px solid ${T.border}` }}>Not applicable for Annex 14 OLS-only assessments.</td></tr></tbody>
                            </table>
                        )}
                    </section>

                    <div className="page-break"></div>

                    {/* Conclusion */}
                    <section aria-labelledby="sec-11">
                        <SectionTitle n="11" title="Conclusion" id="sec-11" />
                        <div style={{ fontSize: 12, lineHeight: 1.6, color: T.white }}>
                            The following aerodromes <strong>{nearby_arps.map(a => `${a.name} (${a.icao})`).join(', ') || 'Nil'}</strong> were detected within the assessment buffers specified in the section Obstacle Screening Parameters.<br /><br />

                            The aerodrome surfaces were assessed with {ols_results.some(r => r.surfaces?.some(s => s.status === 'penetrated')) ? <strong style={{ color: '#e84040' }}>penetrations detected</strong> : 'no impact detected'}.<br />
                            The Obstacle Evaluation Surfaces were assessed with {
                                pans_ops_results.some(r => r.status === 'penetrated') ||
                                    ils_results.some(a => a.procedures?.some(p => p.results?.some(r => r.penetration > 0))) ||
                                    circling_results.some(r => r.results?.some(res => res.evaluations?.some(e => e.penetrates))) ||
                                    rmac_results.some(r => r.is_penetration)
                                    ? <strong style={{ color: '#e84040' }}>penetrations detected</strong> : 'no impact detected'
                            }.<br />
                            The CNS-BRA surfaces were assessed with {bra_results.some(r => r.outcome === 'penetrated') ? <strong style={{ color: '#e84040' }}>penetrations detected</strong> : 'no impact detected'}.
                        </div>
                    </section>

                    {/* Recommendations */}
                    <section aria-labelledby="sec-12">
                        <SectionTitle n="12" title="Recommendations" id="sec-12" />
                        <div style={{ fontSize: 12, lineHeight: 1.6, color: T.white }}>
                            Overall, the proposed development aligns with regulatory requirements and does not pose a threat to the safety or efficiency of aerodrome operations, flight procedures, or CNS systems.<br /><br />
                            Tsela Obstacle Safeguarding recommends the approval of the proposed structure(s).
                        </div>

                        <div style={{ marginTop: 60, pageBreakInside: 'avoid' }}>
                            <h3 style={{ color: T.blue, fontSize: 13, textTransform: 'uppercase', margin: '0 0 10px' }}>Prepared By</h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 40 }}>
                                <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Report preparer signature block</caption>
                                <thead><tr style={{ background: 'rgba(58,138,196,0.12)' }}>
                                    <th scope="col" style={{ padding: 8, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue, width: '25%' }}>Designation</th>
                                    <th scope="col" style={{ padding: 8, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue, width: '25%' }}>Name</th>
                                    <th scope="col" style={{ padding: 8, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue, width: '25%' }}>Date</th>
                                    <th scope="col" style={{ padding: 8, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue, width: '25%' }}>Signature</th>
                                </tr></thead>
                                <tbody><tr>
                                    <td style={{ padding: 8, border: `1px solid ${T.border}` }}>Obstacle Evaluation Specialist</td>
                                    <td style={{ padding: 8, border: `1px solid ${T.border}` }}>G. Mondzinger</td>
                                    <td style={{ padding: 8, border: `1px solid ${T.border}` }}>{now}</td>
                                    <td style={{ padding: 8, border: `1px solid ${T.border}`, height: 40 }}></td>
                                </tr></tbody>
                            </table>

                            <h3 style={{ color: T.blue, fontSize: 13, textTransform: 'uppercase', margin: '0 0 10px' }}>Quality Assured By</h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <caption style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>Quality assurer signature block</caption>
                                <thead><tr style={{ background: 'rgba(58,138,196,0.12)' }}>
                                    <th scope="col" style={{ padding: 8, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue, width: '25%' }}>Designation</th>
                                    <th scope="col" style={{ padding: 8, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue, width: '25%' }}>Name</th>
                                    <th scope="col" style={{ padding: 8, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue, width: '25%' }}>Date</th>
                                    <th scope="col" style={{ padding: 8, border: `1px solid ${T.border}`, textAlign: 'left', color: T.blue, width: '25%' }}>Signature</th>
                                </tr></thead>
                                <tbody><tr>
                                    <td style={{ padding: 8, border: `1px solid ${T.border}` }}>Quality Assurer</td>
                                    <td style={{ padding: 8, border: `1px solid ${T.border}` }}>T. Monyai</td>
                                    <td style={{ padding: 8, border: `1px solid ${T.border}` }}>{now}</td>
                                    <td style={{ padding: 8, border: `1px solid ${T.border}`, height: 40 }}></td>
                                </tr></tbody>
                            </table>
                        </div>
                    </section>

                    {/* Footer */}
                    <footer style={{ marginTop: 40, paddingTop: 16, borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.muted }}>
                        <span>QP/1/3 {proj?.name?.toUpperCase()} - {proj.obstacle_type?.toUpperCase()}</span>
                        </footer>
                </article>
                )}
            </main>

            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    *:not([data-status]) { color: black !important; background: white !important; border-color: #ddd !important; }
                    #report-preview { height: auto !important; overflow: visible !important; }
                    h1 { font-size: 18pt !important; }
                    table { page-break-inside: avoid; }
                    .page-break { page-break-before: always; }
                    .cover-page {
                        height: 297mm !important;
                        display: flex !important;
                        flex-direction: column !important;
                        justify-content: center !important;
                    }
                    @page { margin: auto; size: A4; }
                }
                button:focus-visible {
                    outline: 2px solid ${T.blue};
                    outline-offset: 2px;
                }
            `}</style>
        </div>
    );
}